<template>
  <div class="puq-page">
    <UserAppChrome back-mode="history" :show-dock="false" />
    <div class="puq-card" ref="formRootRef">
      <div class="puq-stepper">
        <button class="puq-stepper__back" type="button" @click="goBack" aria-label="Torna ai programmi">
          <span aria-hidden="true">‹</span>
        </button>
        <div class="puq-stepper__text">
          <p class="puq-stepper__caption">{{ isManualStandalone ? 'Questionario manuale' : 'Questionario iniziale' }}</p>
          <h1 ref="stepHeadingRef" tabindex="-1" class="puq-stepper__title">{{ currentSection.title }}</h1>
          <p class="puq-stepper__subtitle">Sezione {{ activeSectionIndex + 1 }} di {{ sectionFlow.length }}</p>
        </div>
      </div>

      <div class="puq-progress" role="progressbar" aria-label="Avanzamento del questionario" :aria-valuenow="activeSectionIndex + 1" :aria-valuemax="sectionFlow.length" :aria-valuemin="0" :aria-valuetext="`Sezione ${activeSectionIndex + 1} di ${sectionFlow.length}`">
        <div class="puq-progress__track">
          <span class="puq-progress__fill" :style="{ width: progressValue + '%' }"></span>
        </div>
        <span class="puq-progress__label">{{ progressValue }}%</span>
      </div>

      <details class="puq-meta-card">
        <summary>Informazioni e PDF <span aria-hidden="true">⌄</span></summary>
        <div class="puq-meta-content">
        <div class="puq-meta-info">
          <span class="meta-label">Ultimo aggiornamento</span>
          <span class="meta-value">
            <template v-if="questionarioInfo.updatedAt">
              {{ formatDate(questionarioInfo.updatedAt) }}
            </template>
            <template v-else>Nessun dato salvato</template>
          </span>
        </div>
        <div class="puq-meta-actions">
          <button
            type="button"
            class="meta-btn"
            @click="exportFormAsPdf"
            :disabled="pdfExporting || loading || !!errorMessage || !!linkMismatchWarning"
            :aria-busy="pdfExporting"
          >
            <span v-if="pdfExporting" class="meta-btn__spinner" aria-hidden="true"></span>
            <span>{{ pdfExporting ? 'Esporto PDF…' : 'Esporta PDF' }}</span>
          </button>
        </div>
        </div>
      </details>
      <p
        v-if="allowPartialSubmission"
        class="muted"
        style="margin: 0 0 16px;"
      >
        Questo questionario è stato reso parziale: puoi inviare i dati anche senza foto e misure.
      </p>

    <transition name="fade">
      <div
        v-if="actionMessage"
        ref="actionMessageRef"
        tabindex="-1"
        :class="['puq-banner', actionMessageType === 'error' ? 'puq-banner--error' : 'puq-banner--success']"
        :role="actionMessageType === 'error' ? 'alert' : 'status'"
      >
        {{ actionMessage }}
      </div>
    </transition>

    <div
      v-if="linkMismatchWarning"
      class="puq-banner puq-banner--error puq-banner--login"
      role="alert"
    >
      <span>{{ linkMismatchWarning }}</span>
      <router-link class="puq-banner__cta" :to="loginRoute">Vai al login</router-link>
    </div>

    <div v-if="loading" class="puq-state">Caricamento questionario…</div>
    <div v-else-if="errorMessage" class="puq-state puq-state--error">{{ errorMessage }}</div>
    <div v-else-if="linkMismatchWarning" class="puq-state">Accedi per visualizzare il questionario.</div>
    <div v-else-if="lockWarning" class="puq-state puq-state--error">{{ lockWarning }}</div>
    <div
      v-else-if="isAdminApproved"
      class="puq-state puq-state--error"
    >
      Questionario già approvato
    </div>
    <div
      v-else-if="isHardLocked"
      class="puq-state puq-state--error"
    >
      Questionario già inviato: contatta il supporto per eventuali modifiche.
    </div>
    <template v-else>
      <!-- Validation follows the questionnaire mode; hidden sections must not trap native validation. -->
      <form class="qi-form" novalidate @submit.prevent="handleFormSubmit">
              <div v-if="missingRequiredFields.length" ref="requiredSummaryRef" tabindex="-1" class="puq-banner puq-banner--error puq-required-banner" role="alert">
                <p class="puq-required-banner__title">{{ isManualStandalone ? 'Seleziona i consensi obbligatori prima di salvare. Tocca una voce per raggiungerla:' : 'Completa questi campi prima di inviare. Tocca un campo per raggiungerlo:' }}</p>
                <ul class="puq-required-banner__list"><li v-for="field in missingRequiredFields" :key="field.id"><button type="button" class="puq-field-link" @click="goToMissingField(field.id)">{{ field.label }}</button></li></ul>
              </div>
              <!-- Informazioni generali -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('informazioniGenerali') }]"
                v-show="isSectionActive('informazioniGenerali')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Informazioni generali</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label class="birthdate-field birthdate-inline">
                      <span>
                        Data di nascita
                        <span v-if="isFieldRequired('dataNascita')" data-field="dataNascita" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <div class="birthdate-controls birthdate-controls--inline">
                        <select v-model="birthdateParts.day" aria-label="Giorno di nascita">
                          <option value="">GG</option>
                          <option v-for="opt in DAY_OPTIONS" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </option>
                        </select>
                        <select v-model="birthdateParts.month" aria-label="Mese di nascita">
                          <option value="">MM</option>
                          <option v-for="opt in MONTH_OPTIONS" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </option>
                        </select>
                        <select v-model="birthdateParts.year" aria-label="Anno di nascita">
                          <option value="">AAAA</option>
                          <option v-for="opt in YEAR_OPTIONS" :key="opt" :value="opt">
                            {{ opt }}
                          </option>
                        </select>
                      </div>
                    </label>
                    <label>
                      <span>
                        Professione
                        <span v-if="isFieldRequired('professione')" data-field="professione" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input v-model.trim="form.professione" type="text" placeholder="Es. impiegata, studentessa…" />
                    </label>
                    <label>
                      <span>
                        Turnista
                        <span v-if="isFieldRequired('turnista')" data-field="turnista" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.turnista">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in TURNISTA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Ore lavorative / giorno
                        <span v-if="isFieldRequired('oreLavorative')" data-field="oreLavorative" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.oreLavorative">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in WORK_HOURS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Stile di vita
                        <span v-if="isFieldRequired('stileVita')" data-field="stileVita" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.stileVita">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in LIFESTYLE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                      <small class="muted">Una persona dinamica mantiene uno stile attivo anche fuori dall'allenamento.</small>
                    </label>
                    <label>
                      <span>
                        Ore sonno / notte
                        <span v-if="isFieldRequired('oreSonno')" data-field="oreSonno" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.oreSonno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SLEEP_HOURS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Qualità del sonno
                        <span v-if="isFieldRequired('qualitaSonno')" data-field="qualitaSonno" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.qualitaSonno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SLEEP_QUALITY_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Periodi di forte stress
                        <span v-if="isFieldRequired('periodoStressante')" data-field="periodoStressante" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.periodoStressante">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Anamnesi sportiva -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('anamnesiSportiva') }]"
                v-show="isSectionActive('anamnesiSportiva')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Anamnesi sportiva</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Attualmente ti alleni?
                        <span v-if="isFieldRequired('allenamentoAttuale')" data-field="allenamentoAttuale" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.allenamentoAttuale">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ALLENAMENTO_ATTUALE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Esperienza in sala pesi
                        <span v-if="isFieldRequired('esperienzaSala')" data-field="esperienzaSala" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.esperienzaSala">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ESPERIENZA_SALA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ti sei allenata con un personal trainer?</span>
                      <select v-model="form.ptPresenza">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ti sei mai allenata in videochiamata con un personal trainer?</span>
                      <select v-model="form.ptVideocall">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div v-if="form.allenamentoAttuale === 'Si'" class="checkbox-group">
                    <span class="group-label">
                      Tipologia di allenamento
                      <span v-if="isFieldRequired('tipiAllenamento')" data-field="tipiAllenamento" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <label v-for="opt in TIPI_ALLENAMENTO" :key="opt" class="checkbox-item">
                      <input type="checkbox" :value="opt" v-model="form.tipiAllenamento" />
                      <span>{{ opt }}</span>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Anamnesi fisiologica -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('anamnesiFisiologica') }]"
                v-show="isSectionActive('anamnesiFisiologica')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Anamnesi fisiologica</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Alvo (regolarità intestinale)
                        <span v-if="isFieldRequired('alvo')" data-field="alvo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.alvo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ALVO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Ciclo mestruale
                        <span v-if="isFieldRequired('cicloMestruale')" data-field="cicloMestruale" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.cicloMestruale">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in CICLO_MESTRUALE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Litri di acqua al giorno
                        <span v-if="isFieldRequired('litriAcqua')" data-field="litriAcqua" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.litriAcqua">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ACQUA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Fumo
                        <span v-if="isFieldRequired('fumo')" data-field="fumo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.fumo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Bevande alcoliche
                        <span v-if="isFieldRequired('bevandeAlcoliche')" data-field="bevandeAlcoliche" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.bevandeAlcoliche">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ALCOL_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div class="repeater">
                    <header class="qi-subheader">
                      <h3 class="qi-subheader__title">Gravidanze</h3>
                      <button type="button" class="link" @click="addGravidanza">+ Aggiungi gravidanza</button>
                    </header>
                    <div
                      v-for="(item, index) in form.gravidanze"
                      :key="`gravidanza_${index}`"
                      class="repeater-item"
                    >
                      <label>
                        <span>Anno</span>
                        <input v-model="item.anno" type="text" placeholder="Es. 2020" />
                      </label>
                      <label>
                        <span>Tipo di parto</span>
                        <input v-model.trim="item.parto" type="text" placeholder="Es. naturale, cesareo…" />
                      </label>
                      <button type="button" class="link danger" @click="removeGravidanza(index)">Rimuovi</button>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Anamnesi patologica -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('anamnesiPatologica') }]"
                v-show="isSectionActive('anamnesiPatologica')"
              >
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Anamnesi patologica</h2>
                    <p class="qi-block__subtitle muted">
                      Segnala eventuali patologie. Seleziona "Diagnosi certificata" solo se possiedi una certificazione e utilizza il campo dedicato per riportare i dettagli.
                    </p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="accordion-question">
                    <span>Hai patologie metaboliche?</span>
                    <div class="option-toggle">
                      <label
                        :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMetaboliche === 'Si' }]"
                      >
                        <input type="radio" value="Si" v-model="form.haPatologieMetaboliche" />
                        <span>Si</span>
                      </label>
                      <label
                        :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMetaboliche === 'No' }]"
                      >
                        <input type="radio" value="No" v-model="form.haPatologieMetaboliche" />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <transition name="fold">
                    <div v-if="form.haPatologieMetaboliche === 'Si'" class="accordion-body">
                      <div class="pathology-grid">
                        <div v-for="item in PATHOLOGIES" :key="item.key" class="pathology-card">
                          <label class="checkbox-item">
                            <input type="checkbox" v-model="form.patologie[item.key].presente" />
                            <span>{{ item.label }}</span>
                          </label>
                          <div v-if="form.patologie[item.key].presente" class="pathology-body">
                            <label>
                              <span>Diagnosi</span>
                              <select v-model="form.patologie[item.key].diagnosi">
                                <option value="">Seleziona…</option>
                                <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                              </select>
                            </label>
                        <div
                          v-if="form.patologie[item.key].diagnosi === 'Diagnosi certificata'"
                          class="certification-panel"
                        >
                              <div class="certification-upload">
                                <span>Certificazione (file)</span>
                                <input
                                  type="file"
                                  accept=".pdf,image/*"
                                  @change="onCertificationFileChange('patologie', item.key, $event)"
                                />
                                <div
                                  v-if="isCertificationUploading('patologie', item.key)"
                                  class="upload-progress"
                                >
                                  <span class="spinner"></span>
                                  <span>Caricamento…</span>
                                </div>
                                <div
                                  v-else-if="form.patologie[item.key].certificazioneFile"
                                  class="file-meta certification-file-meta"
                                >
                                  <div
                                    v-if="isImageFile(form.patologie[item.key].certificazioneFile)"
                                    class="file-preview"
                                  >
                                    <img
                                      :src="form.patologie[item.key].certificazioneFile.url"
                                      alt="Anteprima certificazione"
                                      loading="lazy"
                                    />
                                  </div>
                                  <div
                                    v-else-if="isPdfFile(form.patologie[item.key].certificazioneFile)"
                                    class="file-preview file-preview--pdf"
                                  >
                                    <iframe
                                      :src="form.patologie[item.key].certificazioneFile.url"
                                      title="Anteprima certificazione PDF"
                                      loading="lazy"
                                    ></iframe>
                                  </div>
                                  <a
                                    :href="form.patologie[item.key].certificazioneFile.url"
                                    target="_blank"
                                    rel="noopener"
                                  >
                                    {{ form.patologie[item.key].certificazioneFile.name }}
                                  </a>
                                  <button
                                    type="button"
                                    class="link danger"
                                    @click="removeCertificationFile('patologie', item.key)"
                                  >
                                    Rimuovi file
                                  </button>
                                </div>
                              </div>
                            </div>
                            <label>
                              <span>Note aggiuntive</span>
                              <textarea
                                v-model.trim="form.patologie[item.key].note"
                                rows="3"
                                placeholder="Informazioni utili allo staff"
                              ></textarea>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Altre patologie</h3>
                          <button type="button" class="link" @click="addAltraPatologia">+ Aggiungi</button>
                        </header>
                        <div
                          v-for="(item, index) in form.patologieAltre"
                          :key="`patologia_extra_${index}`"
                          class="repeater-item"
                        >
                          <label>
                            <span>Patologia</span>
                            <input v-model.trim="item.nome" type="text" placeholder="Nome della patologia" />
                          </label>
                          <label>
                            <span>Diagnosi</span>
                            <select
                              v-model="item.diagnosi"
                              @change="handleDiagnosiChange('patologieAltre', index, $event.target.value)"
                            >
                              <option value="">Seleziona…</option>
                              <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <div v-if="item.diagnosi === 'Diagnosi certificata'" class="certification-panel">
                            <div class="certification-upload">
                              <span>Certificazione (file)</span>
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                @change="onCertificationFileChange('patologieAltre', index, $event)"
                              />
                              <div v-if="isCertificationUploading('patologieAltre', index)" class="upload-progress">
                                <span class="spinner"></span>
                                <span>Caricamento…</span>
                              </div>
                              <div v-else-if="item.certificazioneFile" class="file-meta certification-file-meta">
                                <div v-if="isImageFile(item.certificazioneFile)" class="file-preview">
                                  <img
                                    :src="item.certificazioneFile.url"
                                    alt="Anteprima certificazione"
                                    loading="lazy"
                                  />
                                </div>
                                <div
                                  v-else-if="isPdfFile(item.certificazioneFile)"
                                  class="file-preview file-preview--pdf"
                                >
                                  <iframe
                                    :src="item.certificazioneFile.url"
                                    title="Anteprima certificazione PDF"
                                    loading="lazy"
                                  ></iframe>
                                </div>
                                <a :href="item.certificazioneFile.url" target="_blank" rel="noopener">
                                  {{ item.certificazioneFile.name }}
                                </a>
                                <button
                                  type="button"
                                  class="link danger"
                                  @click="removeCertificationFile('patologieAltre', index)"
                                >
                                  Rimuovi file
                                </button>
                              </div>
                            </div>
                          </div>
                          <label>
                            <span>Note</span>
                            <textarea v-model.trim="item.note" rows="3"></textarea>
                          </label>
                          <button type="button" class="link danger" @click="removeAltraPatologia(index)">Rimuovi</button>
                        </div>
                      </div>

                      <label class="checkbox-item declaration">
                        <input type="checkbox" v-model="form.dichiarazionePatologie" />
                        <span>
                          Dichiaro di aver indicato tutte le patologie conosciute e sollevo la società da responsabilità in caso di dichiarazioni false o mendaci.
                        </span>
                      </label>
                    </div>
                  </transition>
                </div>
              </section>

              <!-- Patologie muscolo-scheletriche -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('patologieMuscolo') }]"
                v-show="isSectionActive('patologieMuscolo')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Patologie muscolo-scheletriche e storico</h2>
                </header>
                <div class="qi-block__body">
                  <div class="accordion-question">
                    <span>Hai patologie muscolo-scheletriche?</span>
                    <div class="option-toggle">
                      <label :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMuscolo === 'Si' }]">
                        <input type="radio" value="Si" v-model="form.haPatologieMuscolo" />
                        <span>Si</span>
                      </label>
                      <label :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMuscolo === 'No' }]">
                        <input type="radio" value="No" v-model="form.haPatologieMuscolo" />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <transition name="fold">
                    <div v-if="form.haPatologieMuscolo === 'Si'" class="accordion-body">
                      <div class="pathology-grid">
                        <div v-for="item in MUSCLE_PATHOLOGIES" :key="item.key" class="pathology-card">
                          <label class="checkbox-item">
                            <input type="checkbox" v-model="form.patologieMuscolo[item.key].presente" />
                            <span>{{ item.label }}</span>
                          </label>
                          <div v-if="form.patologieMuscolo[item.key].presente" class="pathology-body">
                            <label>
                              <span>Diagnosi</span>
                              <select v-model="form.patologieMuscolo[item.key].diagnosi">
                                <option value="">Seleziona…</option>
                                <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                              </select>
                            </label>
                            <label v-if="item.askCm">
                              <span>Centimetri coinvolti</span>
                              <input v-model.trim="form.patologieMuscolo[item.key].cm" type="text" placeholder="Es. 2 cm" />
                            </label>
                            <div
                              v-if="form.patologieMuscolo[item.key].diagnosi === 'Diagnosi certificata'"
                              class="certification-panel"
                            >
                              <div
                                v-if="form.patologieMuscolo[item.key].diagnosi === 'Diagnosi certificata'"
                                class="certification-panel"
                              >
                                <div class="certification-upload">
                                  <span>Certificazione (file)</span>
                                  <input
                                    type="file"
                                    accept=".pdf,image/*"
                                    @change="onCertificationFileChange('patologieMuscolo', item.key, $event)"
                                  />
                                  <div
                                    v-if="isCertificationUploading('patologieMuscolo', item.key)"
                                    class="upload-progress"
                                  >
                                    <span class="spinner"></span>
                                    <span>Caricamento…</span>
                                  </div>
                                  <div
                                    v-else-if="form.patologieMuscolo[item.key].certificazioneFile"
                                    class="file-meta certification-file-meta"
                                  >
                                    <div
                                      v-if="isImageFile(form.patologieMuscolo[item.key].certificazioneFile)"
                                      class="file-preview"
                                    >
                                      <img
                                        :src="form.patologieMuscolo[item.key].certificazioneFile.url"
                                        alt="Anteprima certificazione"
                                        loading="lazy"
                                      />
                                    </div>
                                    <div
                                      v-else-if="isPdfFile(form.patologieMuscolo[item.key].certificazioneFile)"
                                      class="file-preview file-preview--pdf"
                                    >
                                      <iframe
                                        :src="form.patologieMuscolo[item.key].certificazioneFile.url"
                                        title="Anteprima certificazione PDF"
                                        loading="lazy"
                                      ></iframe>
                                    </div>
                                    <a
                                      :href="form.patologieMuscolo[item.key].certificazioneFile.url"
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      {{ form.patologieMuscolo[item.key].certificazioneFile.name }}
                                    </a>
                                    <button
                                      type="button"
                                      class="link danger"
                                      @click="removeCertificationFile('patologieMuscolo', item.key)"
                                    >
                                      Rimuovi file
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <label>
                              <span>Note aggiuntive</span>
                              <textarea v-model.trim="form.patologieMuscolo[item.key].note" rows="3"></textarea>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Altre problematiche muscolo-scheletriche</h3>
                          <button type="button" class="link" @click="addProblematicaMuscolo">+ Aggiungi</button>
                        </header>
                        <div
                          v-for="(item, index) in form.patologieMuscoloAltre"
                          :key="`problematica_${index}`"
                          class="repeater-item"
                        >
                          <label>
                            <span>Zona interessata</span>
                            <input v-model.trim="item.zona" type="text" placeholder="Es. polso destro" />
                          </label>
                          <label>
                            <span>Diagnosi</span>
                            <select
                              v-model="item.diagnosi"
                              @change="handleDiagnosiChange('patologieMuscoloAltre', index, $event.target.value)"
                            >
                              <option value="">Seleziona…</option>
                              <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <div v-if="item.diagnosi === 'Diagnosi certificata'" class="certification-panel">
                            <div class="certification-upload">
                              <span>Certificazione (file)</span>
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                @change="onCertificationFileChange('patologieMuscoloAltre', index, $event)"
                              />
                              <div
                                v-if="isCertificationUploading('patologieMuscoloAltre', index)"
                                class="upload-progress"
                              >
                                <span class="spinner"></span>
                                <span>Caricamento…</span>
                              </div>
                              <div v-else-if="item.certificazioneFile" class="file-meta certification-file-meta">
                                <div v-if="isImageFile(item.certificazioneFile)" class="file-preview">
                                  <img
                                    :src="item.certificazioneFile.url"
                                    alt="Anteprima certificazione"
                                    loading="lazy"
                                  />
                                </div>
                                <div
                                  v-else-if="isPdfFile(item.certificazioneFile)"
                                  class="file-preview file-preview--pdf"
                                >
                                  <iframe
                                    :src="item.certificazioneFile.url"
                                    title="Anteprima certificazione PDF"
                                    loading="lazy"
                                  ></iframe>
                                </div>
                                <a :href="item.certificazioneFile.url" target="_blank" rel="noopener">
                                  {{ item.certificazioneFile.name }}
                                </a>
                                <button
                                  type="button"
                                  class="link danger"
                                  @click="removeCertificationFile('patologieMuscoloAltre', index)"
                                >
                                  Rimuovi file
                                </button>
                              </div>
                            </div>
                          </div>
                          <label>
                            <span>Note</span>
                            <textarea v-model.trim="item.note" rows="3"></textarea>
                          </label>
                          <button type="button" class="link danger" @click="removeProblematicaMuscolo(index)">Rimuovi</button>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Infortuni</h3>
                          <button type="button" class="link" @click="addInfortunio">+ Aggiungi</button>
                        </header>
                        <div v-for="(item, index) in form.infortuni" :key="`infortunio_${index}`" class="repeater-item">
                          <label>
                            <span>Descrizione</span>
                            <textarea v-model.trim="item.descrizione" rows="2"></textarea>
                          </label>
                          <label>
                            <span>Anno</span>
                            <input v-model.trim="item.anno" type="text" placeholder="Es. 2019" />
                          </label>
                          <label>
                            <span>Limitazioni attuali?</span>
                            <select v-model="item.limitazioni">
                              <option value="">Seleziona…</option>
                              <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <button type="button" class="link danger" @click="removeInfortunio(index)">Rimuovi</button>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Interventi</h3>
                          <button type="button" class="link" @click="addIntervento">+ Aggiungi</button>
                        </header>
                        <div v-for="(item, index) in form.interventi" :key="`intervento_${index}`" class="repeater-item">
                          <label>
                            <span>Descrizione</span>
                            <textarea v-model.trim="item.descrizione" rows="2"></textarea>
                          </label>
                          <label>
                            <span>Anno</span>
                            <input v-model.trim="item.anno" type="text" placeholder="Es. 2021" />
                          </label>
                          <label>
                            <span>Limitazioni attuali?</span>
                            <select v-model="item.limitazioni">
                              <option value="">Seleziona…</option>
                              <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <button type="button" class="link danger" @click="removeIntervento(index)">Rimuovi</button>
                        </div>
                      </div>

                      <label>
                        <span>Note su esercizi o movimenti da evitare</span>
                        <textarea v-model.trim="form.noteLimitazioni" rows="3"></textarea>
                      </label>

                      <label>
                        <span>Farmaci assunti (specifica la patologia)</span>
                        <textarea v-model.trim="form.farmaciAssunti" rows="3"></textarea>
                      </label>
                    </div>
                  </transition>
                </div>
              </section>

              <!-- Diario alimentare -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('diarioAlimentare') }]"
                v-show="isSectionActive('diarioAlimentare')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Diario alimentare</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Regime alimentare
                        <span v-if="isFieldRequired('regimeAlimentare')" data-field="regimeAlimentare" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.regimeAlimentare">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in REGIME_ALIMENTARE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Segui un regime alimentare controllato?</span>
                      <select v-model="form.regimeControllato">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Quanti pasti fai al giorno?
                        <span v-if="isFieldRequired('pastiGiorno')" data-field="pastiGiorno" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.pastiGiorno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PASTI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ti capita di saltare i pasti?</span>
                      <select v-model="form.saltarePasti">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SALTARE_PASTI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <label>
                    <span>Regimi controllati seguiti in passato (ed esito)</span>
                    <textarea v-model.trim="form.regimiControllatiPassato" rows="3"></textarea>
                  </label>

                  <p class="diario-hint">
                    Descrivi per ogni momento della giornata cosa mangi precisamente in una giornata tipo, inserisci le grammature se le conosci.
                  </p>

                  <div class="grid two">
                    <label class="diario-label">
                      <span>
                        Colazione
                        <span v-if="isFieldRequired('diarioColazione')" data-field="diarioColazione" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <textarea v-model.trim="form.diarioColazione" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>
                        Metà mattina
                        <span v-if="isFieldRequired('diarioMetaMattina')" data-field="diarioMetaMattina" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <textarea v-model.trim="form.diarioMetaMattina" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>
                        Pranzo
                        <span v-if="isFieldRequired('diarioPranzo')" data-field="diarioPranzo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <textarea v-model.trim="form.diarioPranzo" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>
                        Metà pomeriggio
                        <span v-if="isFieldRequired('diarioMetaPomeriggio')" data-field="diarioMetaPomeriggio" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <textarea v-model.trim="form.diarioMetaPomeriggio" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>
                        Cena
                        <span v-if="isFieldRequired('diarioCena')" data-field="diarioCena" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <textarea v-model.trim="form.diarioCena" rows="3" class="diario-textarea"></textarea>
                    </label>
                  </div>

                  <div class="grid two">
                    <label>
                      <span>Hai mai usato integratori alimentari?</span>
                      <select v-model="form.integratoriUsati">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label v-if="form.integratoriUsati === 'Si'">
                      <span>Quali integratori hai usato?</span>
                      <textarea v-model.trim="form.integratoriUsatiQuali" rows="2"></textarea>
                    </label>
                    <label>
                      <span>Integri attualmente la tua alimentazione?</span>
                      <select v-model="form.integratoriAttuali">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label v-if="form.integratoriAttuali === 'Si'">
                      <span>Quali integratori assumi?</span>
                      <textarea v-model.trim="form.integratoriAttualiQuali" rows="2"></textarea>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Allenamento -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('allenamento') }]"
                v-show="isSectionActive('allenamento')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Il nostro allenamento</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Dove puoi allenarti con la nostra programmazione?
                        <span v-if="isFieldRequired('allenamentoLuogo')" data-field="allenamentoLuogo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.allenamentoLuogo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in LUOGO_ALLENAMENTO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Quante volte puoi allenarti in una settimana?
                        <span v-if="isFieldRequired('allenamentoFrequenza')" data-field="allenamentoFrequenza" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.allenamentoFrequenza">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in FREQUENZA_ALLENAMENTO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Durata massima di ogni seduta (minuti)
                        <span v-if="isFieldRequired('allenamentoDurata')" data-field="allenamentoDurata" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.allenamentoDurata">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in DURATA_ALLENAMENTO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div class="equipment-group">
                    <span class="group-label">Attrezzatura disponibile a casa</span>
                    <div class="equipment-grid">
                      <label v-for="opt in ATTREZZATURE" :key="opt" class="equipment-pill">
                        <input type="checkbox" :value="opt" v-model="form.attrezzaturaCasa" />
                        <span class="equipment-label">{{ opt }}</span>
                      </label>
                    </div>
                    <label v-if="form.attrezzaturaCasa.includes('Altro')" class="equipment-altro">
                      <span>Descrivi l’attrezzatura (Altro)</span>
                      <input
                        type="text"
                        v-model.trim="form.attrezzaturaCasaAltro"
                        placeholder="Es. cyclette, kettlebell, ecc."
                      />
                    </label>
                  </div>

                  <label>
                    <span>
                      Obiettivo del percorso
                      <span v-if="isFieldRequired('obiettivoPercorso')" data-field="obiettivoPercorso" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.obiettivoPercorso" rows="3"></textarea>
                  </label>

                  <label>
                    <span>Alimenti da non introdurre</span>
                    <textarea v-model.trim="form.alimentiDaEscludere" rows="3" placeholder="Es. funghi porcini, derivati del latte…"></textarea>
                  </label>

                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.responsabilitaAlimenti" />
                    <span>Confermo la responsabilità della non introduzione degli alimenti indicati.</span>
                  </label>
                </div>
              </section>

              <!-- Misurazioni -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('misurazioni') }]"
                v-show="isSectionActive('misurazioni')"
              >
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Misurazioni</h2>
                    <p class="qi-block__subtitle muted">
                      Circonferenza addome: 1 cm sopra l'ombelico • Coscia: metà tra anca e ginocchio • Fianchi: punto più largo dell'esterno coscia.
                    </p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Misure prese in preciclo/ciclo?
                        <span v-if="isFieldRequired('misurePreciclo')" data-field="misurePreciclo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.misurePreciclo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Peso (kg)
                        <span v-if="isFieldRequired('peso')" data-field="peso" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.peso"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Altezza (cm)
                        <span v-if="isFieldRequired('altezza')" data-field="altezza" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.altezza"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Fianchi (cm)
                        <span v-if="isFieldRequired('fianchi')" data-field="fianchi" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.fianchi"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Addome (cm)
                        <span v-if="isFieldRequired('addome')" data-field="addome" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.addome"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Torace (cm)
                        <span v-if="isFieldRequired('torace')" data-field="torace" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.torace"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Coscia (cm)
                        <span v-if="isFieldRequired('coscia')" data-field="coscia" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.coscia"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Braccio (cm)
                        <span v-if="isFieldRequired('braccio')" data-field="braccio" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.braccio"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Massa magra (%)
                        <span v-if="isFieldRequired('massaMagra')" data-field="massaMagra" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.massaMagra"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Massa grassa (%)
                        <span v-if="isFieldRequired('massaGrassa')" data-field="massaGrassa" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.massaGrassa"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Acqua corporea (%)
                        <span v-if="isFieldRequired('acquaCorporea')" data-field="acquaCorporea" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.acquaCorporea"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                  </div>
                  <label>
                    <span>Note sulle misurazioni</span>
                    <textarea v-model.trim="form.misurazioniNote" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- Foto -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('foto') }]"
                v-show="isSectionActive('foto')"
              >
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Foto</h2>
                    <p class="photo-intro">Carica foto a figura intera, con buona luce e qualità.</p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="photo-grid">
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto frontale
                        <span v-if="isFieldRequired('foto.anteriore')" data-field="foto.anteriore" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, gambe leggermente divaricate.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('anteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.anteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.anteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.anteriore)" class="file-preview">
                            <img
                              :src="form.foto.anteriore.url"
                              alt="Anteprima foto frontale"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.anteriore.url" target="_blank" rel="noopener">{{ form.foto.anteriore.name }}</a>
                          <button type="button" @click="removePhoto('anteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto posteriore
                        <span v-if="isFieldRequired('foto.posteriore')" data-field="foto.posteriore" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, capelli legati o in avanti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('posteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.posteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.posteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.posteriore)" class="file-preview">
                            <img
                              :src="form.foto.posteriore.url"
                              alt="Anteprima foto posteriore"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.posteriore.url" target="_blank" rel="noopener">{{ form.foto.posteriore.name }}</a>
                          <button type="button" @click="removePhoto('posteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto profilo sinistro
                        <span v-if="isFieldRequired('foto.profiloSinistro')" data-field="foto.profiloSinistro" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloSinistro', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloSinistro" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloSinistro" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloSinistro)" class="file-preview">
                            <img
                              :src="form.foto.profiloSinistro.url"
                              alt="Anteprima foto profilo sinistro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloSinistro.url" target="_blank" rel="noopener">{{ form.foto.profiloSinistro.name }}</a>
                          <button type="button" @click="removePhoto('profiloSinistro')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto profilo destro
                        <span v-if="isFieldRequired('foto.profiloDestro')" data-field="foto.profiloDestro" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloDestro', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloDestro" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloDestro" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloDestro)" class="file-preview">
                            <img
                              :src="form.foto.profiloDestro.url"
                              alt="Anteprima foto profilo destro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloDestro.url" target="_blank" rel="noopener">{{ form.foto.profiloDestro.name }}</a>
                          <button type="button" @click="removePhoto('profiloDestro')">Elimina</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Idoneità -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('idoneita') }]"
                v-show="isSectionActive('idoneita')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Idoneità</h2>
                </header>
                <div class="qi-block__body idoneita-grid">
                  <div class="upload-card">
                    <span>Carica il certificato medico</span>
                    <p class="muted small-text">Accettiamo PDF o immagini. Assicurati che il certificato sia in corso di validità.</p>
                    <input type="file" accept=".pdf,image/*" @change="onIdoneitaFileChange('certificatoMedicoFile', $event)" />
                    <div v-if="uploadingIdoneita.certificatoMedicoFile" class="upload-progress">
                      <span class="spinner"></span>
                      <span>Caricamento…</span>
                    </div>
                    <div v-else-if="form.idoneita.certificatoMedicoFile" class="file-meta">
                      <div v-if="isImageFile(form.idoneita.certificatoMedicoFile)" class="file-preview">
                        <img
                          :src="form.idoneita.certificatoMedicoFile.url"
                          alt="Anteprima certificato medico"
                          loading="lazy"
                        />
                      </div>
                      <div
                        v-else-if="isPdfFile(form.idoneita.certificatoMedicoFile)"
                        class="file-preview file-preview--pdf"
                      >
                        <iframe
                          :src="form.idoneita.certificatoMedicoFile.url"
                          title="Anteprima certificato PDF"
                          loading="lazy"
                        ></iframe>
                      </div>
                      <a :href="form.idoneita.certificatoMedicoFile.url" target="_blank" rel="noopener">{{ form.idoneita.certificatoMedicoFile.name }}</a>
                      <button type="button" @click="removeIdoneitaFile('certificatoMedicoFile')">Elimina</button>
                    </div>
                  </div>

                  <div class="autocert-card">
                    <h3>Completa la tua autocertificazione</h3>
                    <p class="muted small-text">
                      Inserisci i tuoi dati nei campi indicati e mantieni aggiornato questo testo: sarà salvato direttamente all’interno del questionario.
                    </p>
                    <textarea
                      v-model.trim="form.idoneita.autocertificazioneTesto"
                      rows="6"
                      class="autocert-textarea"
                    ></textarea>

                    <div v-if="form.idoneita.autocertificazioneFile" class="file-meta legacy-autocert">
                      <span class="muted">Autocertificazione caricata in precedenza:</span>
                      <div v-if="isImageFile(form.idoneita.autocertificazioneFile)" class="file-preview">
                        <img
                          :src="form.idoneita.autocertificazioneFile.url"
                          alt="Anteprima autocertificazione"
                          loading="lazy"
                        />
                      </div>
                      <div
                        v-else-if="isPdfFile(form.idoneita.autocertificazioneFile)"
                        class="file-preview file-preview--pdf"
                      >
                        <iframe
                          :src="form.idoneita.autocertificazioneFile.url"
                          title="Anteprima autocertificazione PDF"
                          loading="lazy"
                        ></iframe>
                      </div>
                      <a :href="form.idoneita.autocertificazioneFile.url" target="_blank" rel="noopener">{{ form.idoneita.autocertificazioneFile.name }}</a>
                      <button type="button" @click="removeIdoneitaFile('autocertificazioneFile')">Elimina</button>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Privacy -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('privacy') }]"
                v-show="isSectionActive('privacy')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Privacy</h2>
                </header>
                <div class="qi-block__body">
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.datiPersonali" />
                    <span>
                      Presto il consenso al trattamento dei dati personali (obbligatorio).
                      <span v-if="isFieldRequired('privacy.datiPersonali')" data-field="privacy.datiPersonali" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.datiParticolari" />
                    <span>
                      Presto il consenso al trattamento dei dati relativi alla salute (obbligatorio).
                      <span v-if="isFieldRequired('privacy.datiParticolari')" data-field="privacy.datiParticolari" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.marketing" />
                    <span>Presto il consenso al trattamento dei dati per finalità informative e promozionali (facoltativo).</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.comunicazione" />
                    <span>Presto il consenso alla comunicazione dei dati a studi medici accreditati (facoltativo).</span>
                  </label>
                </div>
              </section>

              <!-- Termini -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('termini') }]"
                v-show="isSectionActive('termini')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Termini e condizioni</h2>
                </header>
                <div class="qi-block__body">
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.accettoTermini" />
                    <span>
                      Dichiaro di aver letto e accettato i
                      <a href="https://www.fusco.fit/termini-e-condizioni/" target="_blank" rel="noopener">termini e condizioni</a>
                      e la
                      <a href="https://www.fusco.fit/scheda-servizi/" target="_blank" rel="noopener">scheda servizi</a>.
                      <span v-if="isFieldRequired('accettoTermini')" data-field="accettoTermini" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                  </label>
                </div>
              </section>

              <nav v-if="!isLastSection" class="puq-section-nav" :class="{ 'puq-section-nav--first': isFirstSection }" aria-label="Sezioni del questionario">
                <button
                  v-if="!isFirstSection"
                  type="button"
                  class="nav-button ghost"
                  @click="goToPreviousSection"
                >
                  Indietro
                </button>
                <div class="puq-nav-copy"><span>Sezione {{ activeSectionIndex + 1 }} di {{ sectionFlow.length }}</span><small>Prossima: {{ nextSectionTitle }}</small></div>
                <button type="button" class="nav-button primary" @click="goToNextSection" :disabled="saving || completingStep">
                  Avanti <span aria-hidden="true">→</span>
                </button>
              </nav>

              <footer class="form-footer" v-show="isLastSection">
                <div class="left">
                  <button
                    type="button"
                    class="secondary-button ghost-back"
                    v-if="!isFirstSection"
                    @click="goToPreviousSection"
                  >
                    Indietro
                  </button>
                  <button type="submit" class="primary-button" :disabled="saving || loading">
                    {{ saving ? 'Salvataggio…' : isManualStandalone ? 'Salva questionario' : 'Salva bozza' }}
                  </button>
                  <button
                    v-if="showCompleteButton"
                    type="button"
                    class="primary-button complete-button"
                    :disabled="saving || loading || completingStep || !canCompleteStep"
                    @click="markStepCompleted"
                  >
                    {{ completingStep ? 'Completamento…' : 'Invia Questionario' }}
                  </button>
                  <button
                    type="button"
                    class="secondary-button"
                    :disabled="saving || loading || !dirty"
                    @click="resetForm"
                  >
                    Annulla modifiche
                  </button>
                </div>
                <div class="right">
                  <span v-if="dirty" class="dirty-indicator">Modifiche non salvate</span>
                </div>
                <p v-if="showCompleteButton && !canCompleteStep" class="muted" role="status">Puoi salvare la bozza. Per inviarla, riapri il questionario dalla pagina Percorsi.</p>
              </footer>
      </form>

      <transition name="fade">
        <div v-if="successMessage" class="puq-banner puq-banner--success">{{ successMessage }}</div>
      </transition>
    </template>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import UserAppChrome from './UserAppChrome.vue';
import { findUserFormField, focusUserFormSection } from '@/lib/userFormNavigation';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { setStepDone } from '@/lib/percorsi';
import { buildQuestionarioPdfFileName } from '@/lib/pdfExport';
import { generateQuestionarioPdf } from '@/lib/programmiPdf';
import { computeProgrammeLocks } from '@/lib/programmeLocks';

const QUESTIONARI_COLLECTION = 'questionari';
const QUESTIONARIO_TYPE = 'user_programmi_questionario';
const QUESTIONARIO_TEMPLATE = {
  id: 'user_programmi_questionario_v1',
  key: 'user_programmi_questionario',
  label: 'Questionario Programmi',
  version: 1,
};

const TURNISTA_OPTIONS = ['Si', 'No', 'Turni notturni'];
const WORK_HOURS_OPTIONS = ['Non lavoro', 'Studio', '3', '4', '5', '6', '7', '8', '9', '10', '+10'];
const LIFESTYLE_OPTIONS = ['Sedentario', 'Dinamico'];
const SLEEP_HOURS_OPTIONS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', '+10'];
const SLEEP_QUALITY_OPTIONS = ['Regolare', 'Non regolare', 'Insonnia'];
const YES_NO_OPTIONS = ['Si', 'No','sono uomo'];
const ALLENAMENTO_ATTUALE_OPTIONS = ['Si', 'No', 'Sono ferma da più di un anno'];
const TIPI_ALLENAMENTO = [
  'Allenamento con Pesi',
  'Corpo Libero',
  'Pilates / yoga / posturale',
  'Altri corsi fitness',
  'Corsa',
  'Nuoto',
  'Altro',
];
const ESPERIENZA_SALA_OPTIONS = ['Principiante', 'Intermedio', 'Esperto'];
const PT_OPTIONS = ['Si', 'No', 'No, però mi piacerebbe'];
const ALVO_OPTIONS = ['Regolare', 'Stitico', 'Scariche intestinali'];
const CICLO_MESTRUALE_OPTIONS = [
  'sono uomo',
  'Regolare',
  'Regolare con pillola',
  'Non regolare',
  'Gravidanza in corso',
  'Post parto con allattamento',
  'Post parto senza allattamento',
  'Menopausa',
  'Amenorrea',
];
const ACQUA_OPTIONS = ['1', '2', '3', '4', '4+'];
const ALCOL_OPTIONS = ['Astemio', 'Saltuariamente', 'Solo nei weekend', 'Solo ai pasti', 'Tutti i giorni'];
const DIAGNOSIS_OPTIONS = ['Diagnosi certificata', 'Sospetta o non certificata'];
const REGIME_ALIMENTARE_OPTIONS = ['Onnivoro', 'Vegetariano', 'Vegano'];
const PASTI_OPTIONS = ['1', '2', '3', '4', '5', '5+'];
const SALTARE_PASTI_OPTIONS = ['Mai', 'Raramente', 'Spesso', 'Molto spesso'];
const LUOGO_ALLENAMENTO_OPTIONS = ['Home', 'Gym', 'Sia home che gym'];
const FREQUENZA_ALLENAMENTO_OPTIONS = ['1', '2', '3', '4', '5', '5+'];
const DURATA_ALLENAMENTO_OPTIONS = ['45', '50', '60', '90', '90+'];
const MONTH_OPTIONS = [
  { value: '01', label: '01 - Gennaio' },
  { value: '02', label: '02 - Febbraio' },
  { value: '03', label: '03 - Marzo' },
  { value: '04', label: '04 - Aprile' },
  { value: '05', label: '05 - Maggio' },
  { value: '06', label: '06 - Giugno' },
  { value: '07', label: '07 - Luglio' },
  { value: '08', label: '08 - Agosto' },
  { value: '09', label: '09 - Settembre' },
  { value: '10', label: '10 - Ottobre' },
  { value: '11', label: '11 - Novembre' },
  { value: '12', label: '12 - Dicembre' },
];

const DAY_OPTIONS = Array.from({ length: 31 }, (_, idx) => {
  const value = String(idx + 1).padStart(2, '0');
  return { value, label: value };
});

const buildYearOptions = () => {
  const now = new Date();
  const maxYear = now.getFullYear() - 10;
  const minYear = now.getFullYear() - 90;
  const list = [];
  for (let year = maxYear; year >= minYear; year -= 1) {
    list.push(String(year));
  }
  return list;
};

const YEAR_OPTIONS = buildYearOptions();

const ATTREZZATURE = [
  'Fitball',
  'Glute band',
  'Cavigliere',
  'Manubri',
  'Corda',
  'Bilanciere',
  'TRX',
  'Panca',
  'Elastici di varia resistenza',
  'Altro',
];
const AUTOCERTIFICAZIONE_TEMPLATE = `Autocertificazione
Io sottoscritta/o ______________________ , nata/o a _______________ (____), il __/__/______ e residente a ___________ in via ____________________ n° _____ , dichiaro sotto la mia responsabilità di essere in buona salute ed idonea/o alla pratica dell'attività fisica NON agonistica.`;

const PATHOLOGIES = [
  { key: 'curaFertilita', label: 'Cura fertilità' },
  { key: 'diabete', label: 'Diabete' },
  { key: 'endometriosi', label: 'Endometriosi' },
  { key: 'insulinoResistenza', label: 'Insulino resistenza' },
  { key: 'ipertiroidismo', label: 'Ipertiroidismo' },
  { key: 'ipotiroidismo', label: 'Ipotiroidismo' },
  { key: 'ovaioPolicistico', label: 'Ovaio policistico' },
  { key: 'patologieCardiache', label: 'Patologie cardiache' },
  { key: 'fibromialgia', label: 'Fibromialgia' },
  { key: 'patologieRenali', label: 'Patologie renali' },
  { key: 'tiroideHashimoto', label: 'Tiroide Hashimoto' },
  { key: 'obesita', label: 'Obesità' },
  { key: 'reflussoGastrico', label: 'Reflusso gastrico' },
  { key: 'gastrite', label: 'Gastrite' },
  { key: 'sindromeColonIrr', label: 'Sindrome del colon irritabile (IBS)' },
  { key: 'dca', label: 'Disturbi del comportamento alimentare (DCA)' },
];

const MUSCLE_PATHOLOGIES = [
  { key: 'diastasiAddominale', label: 'Diastasi addominale', askCm: true },
  { key: 'schiena', label: 'Schiena' },
  { key: 'collo', label: 'Collo' },
  { key: 'spalla', label: 'Spalla' },
  { key: 'gomito', label: 'Gomito' },
  { key: 'ginocchio', label: 'Ginocchio' },
  { key: 'caviglia', label: 'Caviglia' },
  { key: 'bacino', label: 'Bacino' },
];

const INVALID_STORAGE_CHARS = /[\\/?%*:|"<>#[\]]/g;
const sanitizeName = (name = '') => {
  const cleaned = name.replace(INVALID_STORAGE_CHARS, '').replace(/\s+/g, '_');
  let result = '';
  for (const ch of cleaned) {
    if (ch.charCodeAt(0) >= 32) result += ch;
  }
  return result;
};

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? null));

const assignDeep = (target, source = {}) => {
  Object.keys(target).forEach((key) => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(targetValue)) {
      target[key] = Array.isArray(sourceValue) ? deepClone(sourceValue) : [];
    } else if (targetValue && typeof targetValue === 'object') {
      assignDeep(targetValue, sourceValue || {});
    } else {
      target[key] = sourceValue !== undefined ? sourceValue : targetValue;
    }
  });
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatDateUtils = (value) => {
  const date = toDateSafe(value);
  if (!date) return '';
  const formatter = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return formatter.format(date);
};

const defaultPathologyState = () => ({
  presente: false,
  diagnosi: '',
  certificazioneFile: null,
  note: '',
});

const defaultMuscoloState = (askCm = false) => ({
  presente: false,
  diagnosi: '',
  certificazioneFile: null,
  note: '',
  cm: askCm ? '' : null,
});

const parseLooseNumber = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const str = String(value).trim();
  if (!str) return NaN;
  const normalized = str.replace(/,/g, '.');
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const isPositiveNumber = (value) => Number.isFinite(parseLooseNumber(value));

const defaultForm = () => ({
  dataNascita: '',
  professione: '',
  turnista: '',
  oreLavorative: '',
  stileVita: '',
  oreSonno: '',
  qualitaSonno: '',
  periodoStressante: '',
  allenamentoAttuale: '',
  tipiAllenamento: [],
  esperienzaSala: '',
  ptPresenza: '',
  ptVideocall: '',
  alvo: '',
  cicloMestruale: '',
  gravidanze: [],
  litriAcqua: '',
  fumo: '',
  bevandeAlcoliche: '',
  patologie: PATHOLOGIES.reduce((acc, item) => {
    acc[item.key] = defaultPathologyState();
    return acc;
  }, {}),
  patologieAltre: [],
  haPatologieMetaboliche: 'No',
  dichiarazionePatologie: false,
  patologieMuscolo: MUSCLE_PATHOLOGIES.reduce((acc, item) => {
    acc[item.key] = defaultMuscoloState(item.askCm);
    return acc;
  }, {}),
  patologieMuscoloAltre: [],
  haPatologieMuscolo: 'No',
  infortuni: [],
  interventi: [],
  noteLimitazioni: '',
  farmaciAssunti: '',
  regimeAlimentare: '',
  regimiControllatiPassato: '',
  regimeControllato: '',
  pastiGiorno: '',
  saltarePasti: '',
  diarioColazione: '',
  diarioMetaMattina: '',
  diarioPranzo: '',
  diarioMetaPomeriggio: '',
  diarioCena: '',
  integratoriUsati: '',
  integratoriUsatiQuali: '',
  integratoriAttuali: '',
  integratoriAttualiQuali: '',
  allenamentoLuogo: '',
  attrezzaturaCasa: [],
  attrezzaturaCasaAltro: '',
  allenamentoFrequenza: '',
  allenamentoDurata: '',
  obiettivoPercorso: '',
  alimentiDaEscludere: '',
  responsabilitaAlimenti: false,
  misurePreciclo: '',
  peso: null,
  altezza: null,
  fianchi: null,
  addome: null,
  torace: null,
  coscia: null,
  braccio: null,
  massaMagra: null,
  massaGrassa: null,
  acquaCorporea: null,
  misurazioniNote: '',
  foto: {
    anteriore: null,
    posteriore: null,
    profiloSinistro: null,
    profiloDestro: null,
  },
  idoneita: {
    autocertificazioneFile: null,
    certificatoMedicoFile: null,
    autocertificazioneTesto: AUTOCERTIFICAZIONE_TEMPLATE,
  },
  privacy: {
    datiPersonali: false,
    datiParticolari: false,
    marketing: false,
    comunicazione: false,
  },
  accettoTermini: false,
});

const makeStoragePath = (uid, docId, ...segments) =>
  ['documenti', uid, 'questionari', docId, ...segments].filter(Boolean).join('/');

const FORM_SECTION_FLOW = Object.freeze([
  { id: 'informazioniGenerali', title: 'Informazioni generali' },
  { id: 'anamnesiSportiva', title: 'Anamnesi sportiva' },
  { id: 'anamnesiFisiologica', title: 'Anamnesi fisiologica' },
  { id: 'anamnesiPatologica', title: 'Anamnesi patologica' },
  { id: 'patologieMuscolo', title: 'Patologie muscolo-scheletriche' },
  { id: 'diarioAlimentare', title: 'Diario alimentare' },
  { id: 'allenamento', title: 'Allenamento' },
  { id: 'misurazioni', title: 'Misurazioni' },
  { id: 'foto', title: 'Foto' },
  { id: 'idoneita', title: 'Idoneità' },
  { id: 'privacy', title: 'Privacy' },
  { id: 'termini', title: 'Termini e condizioni' },
]);

const REQUIRED_FIELDS = Object.freeze([
  { path: 'dataNascita', label: 'Data di nascita' },
  { path: 'professione', label: 'Professione' },
  { path: 'turnista', label: 'Turnista' },
  { path: 'oreLavorative', label: 'Ore lavorative giornaliere' },
  { path: 'stileVita', label: 'Stile di vita' },
  { path: 'oreSonno', label: 'Ore di sonno' },
  { path: 'qualitaSonno', label: 'Qualità del sonno' },
  { path: 'periodoStressante', label: 'Periodo di forte stress' },
  { path: 'allenamentoAttuale', label: 'Allenamento attuale' },
  { path: 'esperienzaSala', label: 'Esperienza in sala pesi' },
  { path: 'alvo', label: 'Alvo' },
  { path: 'cicloMestruale', label: 'Ciclo mestruale' },
  { path: 'litriAcqua', label: 'Litri di acqua al giorno' },
  { path: 'fumo', label: 'Abitudine al fumo' },
  { path: 'bevandeAlcoliche', label: 'Consumo di bevande alcoliche' },
  { path: 'regimeAlimentare', label: 'Regime alimentare' },
  { path: 'pastiGiorno', label: 'Numero di pasti giornalieri' },
  { path: 'diarioColazione', label: 'Colazione (diario alimentare)' },
  { path: 'diarioMetaMattina', label: 'Metà mattina (diario alimentare)' },
  { path: 'diarioPranzo', label: 'Pranzo (diario alimentare)' },
  { path: 'diarioMetaPomeriggio', label: 'Metà pomeriggio (diario alimentare)' },
  { path: 'diarioCena', label: 'Cena (diario alimentare)' },
  { path: 'allenamentoLuogo', label: 'Luogo di allenamento' },
  { path: 'allenamentoFrequenza', label: 'Frequenza di allenamento' },
  { path: 'allenamentoDurata', label: 'Durata media allenamento' },
  { path: 'obiettivoPercorso', label: 'Obiettivo del percorso' },
  { path: 'peso', label: 'Peso', validator: (value) => isPositiveNumber(value) },
  { path: 'altezza', label: 'Altezza', validator: (value) => isPositiveNumber(value) },
  { path: 'fianchi', label: 'Fianchi', validator: (value) => isPositiveNumber(value) },
  { path: 'addome', label: 'Addome', validator: (value) => isPositiveNumber(value) },
  { path: 'coscia', label: 'Coscia', validator: (value) => isPositiveNumber(value) },
  { path: 'foto.anteriore', label: 'Foto frontale', validator: (value) => !!(value && value.url) },
  { path: 'foto.posteriore', label: 'Foto posteriore', validator: (value) => !!(value && value.url) },
  { path: 'foto.profiloSinistro', label: 'Foto profilo sinistro', validator: (value) => !!(value && value.url) },
  { path: 'foto.profiloDestro', label: 'Foto profilo destro', validator: (value) => !!(value && value.url) },
  {
    path: 'tipiAllenamento',
    label: 'Tipologia di allenamento',
    when: (formState) => formState.allenamentoAttuale === 'Si',
    validator: (value) => Array.isArray(value) && value.length > 0,
  },
  {
    path: 'privacy.datiPersonali',
    label: 'Consenso trattamento dati personali',
    validator: (value) => value === true,
  },
  {
    path: 'privacy.datiParticolari',
    label: 'Consenso dati relativi alla salute',
    validator: (value) => value === true,
  },
  {
    path: 'accettoTermini',
    label: 'Accettazione termini e condizioni',
    validator: (value) => value === true,
  },
]);

// Standalone questionnaires require these acknowledgements, not the full programme schema.
const MANUAL_REQUIRED_PATHS = new Set([
  'privacy.datiPersonali',
  'privacy.datiParticolari',
  'accettoTermini',
]);

const PARTIAL_OPTIONAL_PATHS = new Set([
  'misurePreciclo',
  'peso',
  'altezza',
  'fianchi',
  'addome',
  'coscia',
  'foto.anteriore',
  'foto.posteriore',
  'foto.profiloSinistro',
  'foto.profiloDestro',
]);

export default {
  name: 'ProgrammiUserQuestionario',
  components: { UserAppChrome },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const db = getFirestore();
    const storage = getStorage();

    const sectionFlow = FORM_SECTION_FLOW;
    const sectionIndexMap = sectionFlow.reduce((acc, section, index) => {
      acc[section.id] = index;
      return acc;
    }, {});
    const activeSectionIndex = ref(0);
    const formRootRef = ref(null);
    const stepHeadingRef = ref(null);
    const actionMessageRef = ref(null);
    const requiredSummaryRef = ref(null);

    const isFirstSection = computed(() => activeSectionIndex.value === 0);
    const isLastSection = computed(() => activeSectionIndex.value === sectionFlow.length - 1);
    const currentSection = computed(() => sectionFlow[activeSectionIndex.value] || sectionFlow[0]);
    const nextSectionTitle = computed(() =>
      isLastSection.value ? '' : sectionFlow[activeSectionIndex.value + 1]?.title || ''
    );
    const progressValue = computed(() =>
      Math.round(((activeSectionIndex.value + 1) / sectionFlow.length) * 100)
    );

    const scrollToActiveSection = () => {
      if (typeof window === 'undefined') return;
      nextTick(() => {
        focusUserFormSection(stepHeadingRef.value?.closest?.('.puq-stepper') || stepHeadingRef.value || formRootRef.value, { focusTarget: stepHeadingRef.value });
      });
    };

    const goToSectionIndex = (index) => {
      if (index < 0 || index >= sectionFlow.length) return;
      if (index === activeSectionIndex.value) return;
      activeSectionIndex.value = index;
    };

    const goToSection = (idOrIndex) => {
      if (typeof idOrIndex === 'number') {
        goToSectionIndex(idOrIndex);
        return;
      }
      const idx = sectionIndexMap[idOrIndex];
      if (idx == null) return;
      goToSectionIndex(idx);
    };

    const goToNextSection = () => {
      if (isLastSection.value) return;
      goToSectionIndex(activeSectionIndex.value + 1);
    };

    const goToPreviousSection = () => {
      if (isFirstSection.value) return;
      goToSectionIndex(activeSectionIndex.value - 1);
    };
    const handleFormSubmit = () => isLastSection.value ? saveQuestionario() : goToNextSection();
    const goToMissingField = async (fieldId) => {
      const destination = findUserFormField(formRootRef.value, fieldId);
      if (!destination) return;
      goToSectionIndex(destination.sectionIndex);
      // Run after the section-change watcher so its heading scroll cannot override the field.
      await nextTick();
      await nextTick();
      focusUserFormSection(destination.target, { focusTarget: destination.control });
    };

    const isSectionActive = (id) => sectionIndexMap[id] === activeSectionIndex.value;

    const pickQueryValue = (key) => {
      const raw = route.query?.[key];
      if (Array.isArray(raw)) return raw[0] ?? null;
      return raw ?? null;
    };

    const toTrimmedStringOrNull = (value) => {
      if (value === undefined || value === null) return null;
      const str = typeof value === 'string' ? value : String(value);
      const trimmed = str.trim();
      return trimmed ? trimmed : null;
    };

    const isTruthyManualStandalone = (value) => {
      if (value === true) return true;
      if (value === 1) return true;
      if (value === '1') return true;
      if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v === 'true' || v === 'yes';
      }
      return false;
    };

    const joinNameParts = (...parts) => {
      const filtered = parts
        .map((part) => toTrimmedStringOrNull(part))
        .filter(Boolean);
      return filtered.join(' ').trim();
    };

    const toStepIndex = (value) => {
      if (typeof value !== 'number' && typeof value !== 'string') return null;
      if (typeof value === 'string' && !value.trim()) return null;
      const num = Number(value);
      return Number.isSafeInteger(num) && num >= 0 ? num : null;
    };

    const percorsoContext = computed(() => {
      const stepIndex = toStepIndex(pickQueryValue('stepIndex'));
      const sequenceIndex = toStepIndex(pickQueryValue('sequenceIndex'));
      const sequenceTotal = toStepIndex(pickQueryValue('sequenceTotal'));
      return {
        percorsoId: toTrimmedStringOrNull(pickQueryValue('percorsoId')),
        stepIndex,
        stepKey: toTrimmedStringOrNull(pickQueryValue('stepKey')) || 'questionario',
        sequenceGroupId: toTrimmedStringOrNull(pickQueryValue('sequenceGroupId')),
        sequenceIndex,
        sequenceTotal,
        productId: toTrimmedStringOrNull(pickQueryValue('productId')),
        productName: toTrimmedStringOrNull(pickQueryValue('productName')),
        programTitle: toTrimmedStringOrNull(pickQueryValue('programTitle')),
        programLabel: toTrimmedStringOrNull(pickQueryValue('programLabel')),
        plannedStartAt: toTrimmedStringOrNull(pickQueryValue('plannedStartAt')),
        expireAt: toTrimmedStringOrNull(pickQueryValue('expireAt')),
        stepName: toTrimmedStringOrNull(pickQueryValue('stepName')),
      };
    });

    const form = reactive(defaultForm());
    const birthdateParts = reactive({ day: '', month: '', year: '' });
    const lastLoaded = ref(deepClone(form));
    const questionarioDocId = ref('');
    const loadedDocUid = ref('');
    const docPercorsoId = ref('');
    const docStepIndex = ref(null);
    const manualStandalone = ref(false);
    const preferredDocId = computed(() => {
      const candidates = [
        pickQueryValue('questionarioDocId'),
        pickQueryValue('questionarioUid'),
        pickQueryValue('questionnaireDocId'),
        pickQueryValue('questionnaireId'),
        pickQueryValue('docId'),
      ];
      for (const raw of candidates) {
        const value = toTrimmedStringOrNull(raw);
        if (value) return value;
      }
      return null;
    });
    const isManualStandalone = computed(() =>
      isTruthyManualStandalone(pickQueryValue('manualStandalone')) || manualStandalone.value
    );
    const showCompleteButton = computed(() => !!questionarioDocId.value && !isManualStandalone.value);
    const questionarioInfo = ref({ createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label });
    const allowPartialSubmission = ref(false);
    const setDocContext = (data = {}) => {
      docPercorsoId.value = toTrimmedStringOrNull(data?.percorsoId) || '';
      docStepIndex.value = toStepIndex(data?.stepIndex);
    };
    const clearDocContext = () => {
      docPercorsoId.value = '';
      docStepIndex.value = null;
    };
    const completingStep = ref(false);
    const pdfExporting = ref(false);
    const canCompleteStep = computed(() => {
      const ctx = percorsoContext.value;
      return !!(ctx.percorsoId && Number.isInteger(ctx.stepIndex));
    });
    const missingRequiredFields = ref([]);
    const requiredFieldMap = REQUIRED_FIELDS.reduce((acc, field) => {
      acc[field.path] = field;
      return acc;
    }, {});

    const isFieldRequired = (path, state = form) => {
      if (isManualStandalone.value && !MANUAL_REQUIRED_PATHS.has(path)) return false;
      if (allowPartialSubmission.value && PARTIAL_OPTIONAL_PATHS.has(path)) {
        return false;
      }
      const field = requiredFieldMap[path];
      if (!field) return false;
      if (typeof field.when === 'function') {
        return !!field.when(state);
      }
      return true;
    };

    const toPathSegments = (path) => {
      if (Array.isArray(path)) return path;
      return String(path)
        .split('.')
        .map((segment) => segment.trim())
        .filter(Boolean);
    };

    const readValueByPath = (state, path) =>
      toPathSegments(path).reduce((acc, segment) => (acc == null ? acc : acc[segment]), state);

    const isValueEmpty = (value) => {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string') return value.trim().length === 0;
      if (Array.isArray(value)) return value.length === 0;
      return false;
    };

    const validateRequiredFields = (state = form) => {
      const errors = [];
      REQUIRED_FIELDS.forEach((field) => {
        if (isManualStandalone.value && !MANUAL_REQUIRED_PATHS.has(field.path)) return;
        if (allowPartialSubmission.value && PARTIAL_OPTIONAL_PATHS.has(field.path)) {
          return;
        }
        if (field.when && !field.when(state)) return;
        const value = readValueByPath(state, field.path);
        const valid = field.validator ? field.validator(value, state) : !isValueEmpty(value);
        if (!valid) {
          errors.push({
            id: field.id || field.path,
            label: field.label,
            message: field.message || `Compila il campo “${field.label}”.`,
          });
        }
      });
      return errors;
    };

    const alertMissingFields = (errors) => {
      if (errors.length) nextTick(() => focusUserFormSection(requiredSummaryRef.value));
    };

    const loading = ref(true);
    const saving = ref(false);
    const dirty = ref(false);
    const errorMessage = ref('');
    const lockWarning = ref('');
    const successMessage = ref('');
    let successTimer = null;

    const actionMessage = ref('');
    const actionMessageType = ref('success');
    let actionTimer = null;

    const suppressDirty = ref(false);
    const submittedLock = ref(false);
    const stepDoneFlag = ref(false);
    const adminNeedsUpdate = ref(false);
    const adminApproved = ref(false);
    const approvalAlerted = ref(false);
    const submittedLockMessage =
      'Questionario già inviato: contatta il supporto per eventuali modifiche.';
    const approvalLockMessage = 'Questionario già approvato';
    const isHardLocked = computed(() => submittedLock.value && !adminNeedsUpdate.value);
    const isAdminApproved = computed(() => adminApproved.value);

    const uploadingPhoto = reactive({});
    const uploadingCertification = reactive({});
    const uploadingIdoneita = reactive({});

    const setUploadState = (store, key, value) => {
      store[key] = value;
    };

    const certificationUploadKey = (group, identifier) => `${group}__${identifier}`;
    const isCertificationUploading = (group, identifier) =>
      !!uploadingCertification[certificationUploadKey(group, identifier)];

    const isImageFile = (metadata) => {
      if (!metadata) return false;
      const type = String(metadata.contentType || '').toLowerCase();
      if (type.startsWith('image/')) return true;
      const name = String(metadata.name || '').toLowerCase();
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
    };

    const isPdfFile = (metadata) => {
      if (!metadata) return false;
      const type = String(metadata.contentType || '').toLowerCase();
      if (type === 'application/pdf') return true;
      const name = String(metadata.name || '').toLowerCase();
      return name.endsWith('.pdf');
    };

    const auth = getAuth();
    const currentUserId = ref(auth.currentUser?.uid || '');
    const authResolved = ref(false);
    const linkUserId = computed(() => toTrimmedStringOrNull(pickQueryValue('userId')));
    // preferisci sempre l'uid passato nel link per risolvere l'utente target
    const effectiveUserId = computed(() => linkUserId.value || loadedDocUid.value || currentUserId.value || '');
    const explicitLinkUserName = computed(() => {
      const combined = joinNameParts(pickQueryValue('userName'), pickQueryValue('userSurname'));
      if (combined) return combined;
      return toTrimmedStringOrNull(pickQueryValue('userFullName'));
    });
    const fetchedLinkUserName = ref('');
    let lastIdentityRequest = 0;
    watch(
      [linkUserId, explicitLinkUserName],
      async ([targetUid, explicitName]) => {
        lastIdentityRequest += 1;
        const requestId = lastIdentityRequest;
        if (!targetUid || explicitName) {
          fetchedLinkUserName.value = '';
          return;
        }
        try {
          const snap = await getDoc(doc(db, 'users', targetUid));
          if (requestId !== lastIdentityRequest) return;
          if (snap.exists()) {
            const data = snap.data() || {};
            fetchedLinkUserName.value =
              joinNameParts(data.name || data.firstName, data.surname || data.lastName) ||
              toTrimmedStringOrNull(data.displayName) ||
              '';
          } else {
            fetchedLinkUserName.value = '';
          }
        } catch (err) {
          if (requestId === lastIdentityRequest) {
            fetchedLinkUserName.value = '';
          }
          console.warn('Impossibile recuperare il nome collegato al questionario:', err);
        }
      },
      { immediate: true }
    );
    const linkUserDisplayName = computed(
      () => explicitLinkUserName.value || fetchedLinkUserName.value || 'questo utente'
    );
    const loginRoute = computed(() => ({
      path: '/',
      query: route.fullPath ? { next: route.fullPath } : undefined,
    }));
    const linkMismatchWarning = computed(() => {
      const targetUid = linkUserId.value;
      if (!targetUid) return '';
      if (!authResolved.value) return '';
      const currentUid = currentUserId.value || '';
      const resolvedUid = effectiveUserId.value || '';
      if (resolvedUid === targetUid || currentUid === targetUid) return '';
      if (!currentUid) {
        return `Questionario di "${linkUserDisplayName.value}": per compilarlo effettua il login, altrimenti contatta l'assistenza per ricevere il tuo link.`;
      }
      return `Questionario di "${linkUserDisplayName.value}": per compilarlo effettua il login, altrimenti contatta l'assistenza per ricevere il tuo link.`;
    });

    const evaluatePercorsoLock = async () => {
      const ctx = percorsoContext.value;
      const uid = effectiveUserId.value;
      const percorsoId = ctx.percorsoId || docPercorsoId.value;
      const stepIndex = Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : docStepIndex.value;
      if (!uid || !percorsoId) {
        lockWarning.value = '';
        submittedLock.value = false;
        adminNeedsUpdate.value = false;
        stepDoneFlag.value = false;
        adminApproved.value = false;
        approvalAlerted.value = false;
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, 'percorsi'), where('uid', '==', uid)));
        const percorsi = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const target = percorsi.find(p => p.id === percorsoId);
        if (!target) {
          lockWarning.value = '';
          submittedLock.value = false;
          adminNeedsUpdate.value = false;
          stepDoneFlag.value = false;
          adminApproved.value = false;
          approvalAlerted.value = false;
          return;
        }
        if (target.historyLocked) {
          lockWarning.value = 'Prima di compilare questo hai dei questionari o check da completare';
          adminApproved.value = false;
          approvalAlerted.value = false;
          return;
        }
        const { sequentialLocks, previousLocks } = computeProgrammeLocks(percorsi);
        const isBlocked = previousLocks[target.id] || sequentialLocks[target.id];
        lockWarning.value = isBlocked ? 'Prima di compilare questo hai dei questionari o check da completare' : '';
        const steps = Array.isArray(target.steps) ? target.steps : [];
        const step = Number.isInteger(stepIndex) ? steps[stepIndex] : null;
        stepDoneFlag.value = !!(step && step.done);
        adminNeedsUpdate.value = (step?.adminStatus || '') === 'needs_update';
        adminApproved.value = (step?.adminStatus || '') === 'approved';
        submittedLock.value = stepDoneFlag.value && !adminNeedsUpdate.value;
        if (adminApproved.value) {
          showApprovedLockMessage();
        } else {
          approvalAlerted.value = false;
        }
      } catch (err) {
        console.warn('Impossibile verificare il blocco del percorso', err);
        lockWarning.value = '';
        submittedLock.value = false;
        adminNeedsUpdate.value = false;
        stepDoneFlag.value = false;
        adminApproved.value = false;
        approvalAlerted.value = false;
      }
    };
    const questionarioTargetKey = computed(() => {
      const ctx = percorsoContext.value;
      return [
        effectiveUserId.value || '',
        ctx.percorsoId || '',
        Number.isInteger(ctx.stepIndex) ? String(ctx.stepIndex) : 'x',
        ctx.stepKey || '',
      ].join('__');
    });

    let detachAuthListener = null;
    const showActionMessage = (message, type = 'success') => {
      if (actionTimer) clearTimeout(actionTimer);
      actionMessageType.value = type;
      actionMessage.value = message;
      if (message && type !== 'error') {
        actionTimer = setTimeout(() => {
          actionMessage.value = '';
        }, 4000);
      }
      if (message && type === 'error') nextTick(() => focusUserFormSection(actionMessageRef.value));
    };
    const showApprovedLockMessage = () => {
      showActionMessage(approvalLockMessage, 'error');
      if (typeof window !== 'undefined' && !approvalAlerted.value) {
        window.alert(approvalLockMessage);
        approvalAlerted.value = true;
      }
    };

    onBeforeRouteLeave(() => !dirty.value || typeof window === 'undefined' || window.confirm('Hai modifiche non salvate. Uscire comunque dal questionario?'));
    const goBack = () => {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        router.back();
      } else {
        router.push('/user-programmi');
      }
    };

    const plainAnswers = () => deepClone(form);

    const exportFormAsPdf = async () => {
      if (pdfExporting.value) return;
      pdfExporting.value = true;
      showActionMessage('Sto generando il PDF…');
      try {
        const exportUserId = effectiveUserId.value || questionarioDocId.value || 'utente';
        const fileName = buildQuestionarioPdfFileName({
          prefix: 'questionario_iniziale',
          userId: exportUserId,
          updatedAt: questionarioInfo.value.updatedAt,
        });
        await generateQuestionarioPdf({
          answers: plainAnswers(),
          meta: {
            userId: exportUserId,
            userLabel: linkUserDisplayName.value,
            questionarioInfo: questionarioInfo.value,
            percorso: percorsoContext.value,
          },
          fileName,
        });
        showActionMessage('PDF salvato nei download.');
      } catch (err) {
        console.error('exportFormAsPdf error:', err);
        showActionMessage('Impossibile generare il PDF.', 'error');
      } finally {
        pdfExporting.value = false;
      }
    };

    const buildQuestionarioMetadata = () => {
      const ctx = percorsoContext.value;
      const meta = {
        percorsoId: ctx.percorsoId || null,
        stepIndex: Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null,
        stepKey: ctx.stepKey || null,
        sequenceGroupId: ctx.sequenceGroupId || null,
        sequenceIndex: Number.isInteger(ctx.sequenceIndex) ? ctx.sequenceIndex : null,
        sequenceTotal: Number.isInteger(ctx.sequenceTotal) ? ctx.sequenceTotal : null,
        productId: ctx.productId || null,
        productName: ctx.productName || null,
        programTitle: ctx.programTitle || ctx.programLabel || null,
        programLabel: ctx.programLabel || ctx.programTitle || null,
        plannedStartAt: ctx.plannedStartAt || null,
        expireAt: ctx.expireAt || null,
        stepName: ctx.stepName || null,
      };
      meta.questionarioKey =
        meta.percorsoId && Number.isInteger(meta.stepIndex)
          ? `${meta.percorsoId}__${meta.stepIndex}`
          : meta.percorsoId && meta.stepKey
          ? `${meta.percorsoId}__${meta.stepKey}`
          : null;
      return meta;
    };

    const toIsoString = (value) => {
      const date = toDateSafe(value);
      return date ? date.toISOString() : null;
    };

    const syncQuestionarioStepMetadata = async (docId, overrides = {}) => {
      const ctx = percorsoContext.value;
      if (!docId || !ctx.percorsoId || ctx.stepIndex == null || ctx.stepIndex < 0) return;
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? [...data.steps] : [];
        const idx = ctx.stepIndex;
        const prev = steps[idx] || {};
        const prevQuestionnaire = prev.questionnaire || {};
        const baseMeta = buildQuestionarioMetadata();
        const mergedMeta = {
          ...prevQuestionnaire,
          ...baseMeta,
          ...overrides,
        };

        mergedMeta.id = docId;
        mergedMeta.type = QUESTIONARIO_TYPE;
        mergedMeta.questionarioUid = docId;
        mergedMeta.percorsoId = mergedMeta.percorsoId || ctx.percorsoId;
        mergedMeta.stepIndex = Number.isInteger(mergedMeta.stepIndex)
          ? mergedMeta.stepIndex
          : Number.isInteger(ctx.stepIndex)
          ? ctx.stepIndex
          : null;
        mergedMeta.stepKey = mergedMeta.stepKey || ctx.stepKey || null;
        mergedMeta.sequenceIndex =
          Number.isInteger(mergedMeta.sequenceIndex) ? mergedMeta.sequenceIndex : Number.isInteger(ctx.sequenceIndex) ? ctx.sequenceIndex : null;
        mergedMeta.sequenceTotal =
          Number.isInteger(mergedMeta.sequenceTotal) ? mergedMeta.sequenceTotal : Number.isInteger(ctx.sequenceTotal) ? ctx.sequenceTotal : null;
        mergedMeta.sequenceGroupId = mergedMeta.sequenceGroupId || ctx.sequenceGroupId || null;
        mergedMeta.productId = mergedMeta.productId || ctx.productId || null;
        mergedMeta.productName = mergedMeta.productName || ctx.productName || null;
        mergedMeta.programTitle = mergedMeta.programTitle || ctx.programTitle || ctx.programLabel || null;
        mergedMeta.programLabel = mergedMeta.programLabel || ctx.programLabel || ctx.programTitle || null;
        mergedMeta.plannedStartAt = mergedMeta.plannedStartAt || ctx.plannedStartAt || null;
        mergedMeta.expireAt = mergedMeta.expireAt || ctx.expireAt || null;
        mergedMeta.stepName = mergedMeta.stepName || ctx.stepName || null;

        if (!mergedMeta.questionarioKey) {
          const qKey =
            mergedMeta.percorsoId && Number.isInteger(mergedMeta.stepIndex)
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepIndex}`
              : mergedMeta.percorsoId && mergedMeta.stepKey
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepKey}`
              : null;
          mergedMeta.questionarioKey = qKey;
        }

        if (overrides.updatedAt) {
          mergedMeta.updatedAt = toIsoString(overrides.updatedAt);
        } else if (!mergedMeta.updatedAt) {
          mergedMeta.updatedAt = toIsoString(new Date());
        }

        if (overrides.createdAt) {
          mergedMeta.createdAt = toIsoString(overrides.createdAt);
        } else if (!mergedMeta.createdAt && questionarioInfo.value.createdAt) {
          mergedMeta.createdAt = toIsoString(questionarioInfo.value.createdAt);
        }

        mergedMeta.templateId =
          overrides.templateId ??
          mergedMeta.templateId ??
          QUESTIONARIO_TEMPLATE.id;
        mergedMeta.templateKey =
          overrides.templateKey ??
          mergedMeta.templateKey ??
          QUESTIONARIO_TEMPLATE.key;
        mergedMeta.templateLabel =
          overrides.templateLabel ??
          mergedMeta.templateLabel ??
          QUESTIONARIO_TEMPLATE.label;
        mergedMeta.templateVersion =
          overrides.templateVersion ??
          mergedMeta.templateVersion ??
          QUESTIONARIO_TEMPLATE.version;

        const healthMeta = buildHealthSnapshot();
        mergedMeta.patologieMetaboliche = healthMeta.patologieMetaboliche;
        mergedMeta.patologieMuscolo = healthMeta.patologieMuscolo;
        mergedMeta.hasPatologieMetaboliche = healthMeta.hasPatologieMetaboliche;
        mergedMeta.hasPatologieMuscolo = healthMeta.hasPatologieMuscolo;

        steps[idx] = {
          ...prev,
          questionarioDocId: docId,
          questionnaire: mergedMeta,
        };

        await updateDoc(percorsoRef, { steps });
      } catch (error) {
        console.error('Errore aggiornando metadati questionario nel percorso:', error);
      }
    };

    const maybeBackfillQuestionarioDoc = async (docId, data = {}) => {
      if (!docId) return;
      const metadata = buildQuestionarioMetadata();
      const patch = {};
      let needsPatch = false;

      const healthSnapshot = buildHealthSnapshot();
      const combinedMeta = { ...metadata, ...healthSnapshot };

      Object.entries(combinedMeta).forEach(([key, value]) => {
        if (value == null) return;
        const existing = data[key];
        if (existing == null || (typeof existing === 'string' && !existing.trim())) {
          patch[key] = value;
          needsPatch = true;
        }
      });

      if (!data.questionarioUid) {
        patch.questionarioUid = docId;
        needsPatch = true;
      }
      if (metadata.questionarioKey && !data.questionarioKey) {
        patch.questionarioKey = metadata.questionarioKey;
        needsPatch = true;
      }

      if (needsPatch) {
        try {
          await updateDoc(doc(db, QUESTIONARI_COLLECTION, docId), patch);
        } catch (err) {
          console.warn('Impossibile aggiornare i metadati del questionario:', err);
        }
      }
    };

    const buildQuestionarioUpdatePayload = (extra = {}) => {
      const base = {
        ...buildQuestionarioMetadata(),
        questionarioUid: questionarioDocId.value || null,
        ...buildHealthSnapshot(),
        adminStatus: null,
        ...extra,
      };
      if (!('updatedAt' in base)) {
        base.updatedAt = serverTimestamp();
      }
      return base;
    };

    const clearAdminStatusOnStep = async () => {
      if (!adminNeedsUpdate.value) return;
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) return;
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? [...data.steps] : [];
        const idx = ctx.stepIndex;
        if (!steps[idx]) return;
        steps[idx] = { ...steps[idx], adminStatus: 'updated' };
        await updateDoc(percorsoRef, { steps });
        adminNeedsUpdate.value = false;
        submittedLock.value = stepDoneFlag.value;
      } catch (err) {
        console.warn('clearAdminStatusOnStep error', err);
      }
    };

    const normalizeYesNo = (value) => {
      const str = String(value ?? '').trim().toLowerCase();
      if (!str) return '';
      if (['si', 'sì', 'yes', 'true', '1'].includes(str)) return 'Si';
      if (['no', 'false', '0'].includes(str)) return 'No';
      return '';
    };

    const buildHealthSnapshot = () => {
      const metab = normalizeYesNo(form.haPatologieMetaboliche) || 'No';
      const muscolo = normalizeYesNo(form.haPatologieMuscolo) || 'No';
      return {
        patologieMetaboliche: metab,
        patologieMuscolo: muscolo,
        hasPatologieMetaboliche: metab === 'Si',
        hasPatologieMuscolo: muscolo === 'Si',
      };
    };

    const syncBirthdatePartsFromValue = (value) => {
      if (!value) {
        birthdateParts.day = '';
        birthdateParts.month = '';
        birthdateParts.year = '';
        return;
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        birthdateParts.day = '';
        birthdateParts.month = '';
        birthdateParts.year = '';
        return;
      }
      birthdateParts.year = String(parsed.getUTCFullYear());
      birthdateParts.month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
      birthdateParts.day = String(parsed.getUTCDate()).padStart(2, '0');
    };

    const updateBirthdateFromParts = () => {
      if (birthdateParts.year && birthdateParts.month && birthdateParts.day) {
        form.dataNascita = `${birthdateParts.year}-${birthdateParts.month}-${birthdateParts.day}`;
      } else {
        form.dataNascita = '';
      }
    };

    const setFormData = (data, fullDoc = null) => {
      const base = defaultForm();
      assignDeep(base, data || {});
      const legacyProfile = data?.foto?.profilo;
      if (legacyProfile) {
        if (!base.foto.profiloDestro) base.foto.profiloDestro = legacyProfile;
        if (!base.foto.profiloSinistro) base.foto.profiloSinistro = legacyProfile;
      }

      suppressDirty.value = true;
      assignDeep(form, base);
      lastLoaded.value = deepClone(base);

      syncBirthdatePartsFromValue(form.dataNascita);

      PATHOLOGIES.forEach((item) =>
        handleDiagnosiChange('patologie', item.key, form.patologie[item.key].diagnosi),
      );
      form.patologieAltre.forEach((entry, index) =>
        handleDiagnosiChange('patologieAltre', index, entry.diagnosi),
      );
      MUSCLE_PATHOLOGIES.forEach((item) =>
        handleDiagnosiChange('patologieMuscolo', item.key, form.patologieMuscolo[item.key].diagnosi),
      );
      form.patologieMuscoloAltre.forEach((entry, index) =>
        handleDiagnosiChange('patologieMuscoloAltre', index, entry.diagnosi),
      );

      if (fullDoc) {
        const metab = normalizeYesNo(fullDoc.patologieMetaboliche);
        if (metab) form.haPatologieMetaboliche = metab;
        const muscolo = normalizeYesNo(fullDoc.patologieMuscolo);
        if (muscolo) form.haPatologieMuscolo = muscolo;
      }

      const hasMetabolicData =
        (data &&
          PATHOLOGIES.some((item) => data.patologie?.[item.key]?.presente || data.patologie?.[item.key]?.diagnosi)) ||
        (data && Array.isArray(data.patologieAltre) && data.patologieAltre.length > 0);
      const derivedMetab = hasMetabolicData ? 'Si' : 'No';
      if (!normalizeYesNo(form.haPatologieMetaboliche)) {
        form.haPatologieMetaboliche = derivedMetab;
      }

      const hasMuscoloData =
        (data &&
          MUSCLE_PATHOLOGIES.some(
            (item) => data.patologieMuscolo?.[item.key]?.presente || data.patologieMuscolo?.[item.key]?.diagnosi,
          )) ||
        (data && Array.isArray(data.patologieMuscoloAltre) && data.patologieMuscoloAltre.length > 0) ||
        (data && Array.isArray(data.infortuni) && data.infortuni.length > 0) ||
        (data && Array.isArray(data.interventi) && data.interventi.length > 0) ||
        (data && (data.noteLimitazioni || data.farmaciAssunti));
      const derivedMuscolo = hasMuscoloData ? 'Si' : 'No';
      if (!normalizeYesNo(form.haPatologieMuscolo)) {
        form.haPatologieMuscolo = derivedMuscolo;
      }

      if (!form.idoneita.autocertificazioneTesto) {
        form.idoneita.autocertificazioneTesto = AUTOCERTIFICAZIONE_TEMPLATE;
      }

      dirty.value = false;
      nextTick(() => {
        suppressDirty.value = false;
      });
    };

    const ensureQuestionarioDoc = async () => {
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        throw new Error('Questionario già approvato.');
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        throw new Error('Questionario già inviato.');
      }
      if (questionarioDocId.value) return questionarioDocId.value;
      const uid = effectiveUserId.value;
      if (!uid) throw new Error('ID utente non disponibile.');
      const docRef = doc(collection(db, QUESTIONARI_COLLECTION));
      const metadata = buildQuestionarioMetadata();
      await setDoc(docRef, {
        uid,
        type: QUESTIONARIO_TYPE,
        templateId: QUESTIONARIO_TEMPLATE.id,
        templateKey: QUESTIONARIO_TEMPLATE.key,
        templateLabel: QUESTIONARIO_TEMPLATE.label,
        templateVersion: QUESTIONARIO_TEMPLATE.version,
        answers: plainAnswers(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...metadata,
        questionarioUid: docRef.id,
        ...buildHealthSnapshot(),
      });
      const now = new Date();
      questionarioDocId.value = docRef.id;
      loadedDocUid.value = uid;
      questionarioInfo.value = { createdAt: now, updatedAt: now, templateLabel: QUESTIONARIO_TEMPLATE.label };
      allowPartialSubmission.value = false;
      await syncQuestionarioStepMetadata(docRef.id, {
        ...metadata,
        templateId: QUESTIONARIO_TEMPLATE.id,
        templateKey: QUESTIONARIO_TEMPLATE.key,
        templateLabel: QUESTIONARIO_TEMPLATE.label,
        templateVersion: QUESTIONARIO_TEMPLATE.version,
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    };

    const loadQuestionario = async () => {
      let uid = effectiveUserId.value;
      loading.value = true;
      errorMessage.value = '';
      lockWarning.value = '';
      submittedLock.value = false;
      clearDocContext();
      successMessage.value = '';
      manualStandalone.value = isTruthyManualStandalone(pickQueryValue('manualStandalone'));

      const tryLoadPreferredDoc = async () => {
        if (!preferredDocId.value) return false;
        try {
          const snap = await getDoc(doc(db, QUESTIONARI_COLLECTION, preferredDocId.value));
          if (!snap.exists()) return false;
          const data = snap.data() || {};
          if (data.type !== QUESTIONARIO_TYPE) return false;
          if (data.uid && currentUserId.value !== data.uid) currentUserId.value = data.uid;
          loadedDocUid.value = data.uid || '';
          questionarioDocId.value = snap.id;
          setDocContext(data);
          questionarioInfo.value = {
            createdAt: toDateSafe(data.createdAt) || null,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || null,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
          };
          setFormData(data.answers || {}, data);
          allowPartialSubmission.value = !!data.allowPartial;
          manualStandalone.value = isTruthyManualStandalone(data.manualStandalone) || manualStandalone.value;
          await maybeBackfillQuestionarioDoc(snap.id, data);
          await syncQuestionarioStepMetadata(snap.id, {
            templateId: data.templateId || QUESTIONARIO_TEMPLATE.id,
            templateKey: data.templateKey || data.questionarioTemplateKey || QUESTIONARIO_TEMPLATE.key,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
            templateVersion: data.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
            createdAt: toDateSafe(data.createdAt) || undefined,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || undefined,
            questionarioUid: data.questionarioUid || snap.id,
          });
          return true;
        } catch (err) {
          console.warn('Impossibile caricare il questionario preferito:', err);
          return false;
        }
      };

      const loadedPreferred = await tryLoadPreferredDoc();
      if (loadedPreferred) {
        await evaluatePercorsoLock();
        if (lockWarning.value) {
          loading.value = false;
          return;
        }
        loading.value = false;
        return;
      }

      if (!uid) {
        loading.value = false;
        return;
      }

      await evaluatePercorsoLock();
      if (lockWarning.value) {
        loading.value = false;
        return;
      }

      try {
        const ctx = percorsoContext.value;
        const candidateMap = new Map();

        if (ctx.percorsoId && ctx.stepKey) {
          const constraints = [
            where('uid', '==', uid),
            where('type', '==', QUESTIONARIO_TYPE),
            where('percorsoId', '==', ctx.percorsoId),
            where('stepKey', '==', ctx.stepKey),
          ];
          if (Number.isInteger(ctx.stepIndex)) {
            constraints.push(where('stepIndex', '==', ctx.stepIndex));
          }
          const targetedSnap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), ...constraints));
          targetedSnap.forEach((docSnap) => {
            candidateMap.set(docSnap.id, docSnap);
          });
        }

        if (!candidateMap.size) {
          try {
            const orderedSnap = await getDocs(
              query(
                collection(db, QUESTIONARI_COLLECTION),
                where('uid', '==', uid),
                where('type', '==', QUESTIONARIO_TYPE),
                orderBy('updatedAt', 'desc'),
              ),
            );
            orderedSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
          } catch (err) {
            if (err?.code === 'failed-precondition') {
              const fallbackSnap = await getDocs(
                query(
                  collection(db, QUESTIONARI_COLLECTION),
                  where('uid', '==', uid),
                  where('type', '==', QUESTIONARIO_TYPE),
                ),
              );
              fallbackSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
            } else {
              throw err;
            }
          }
        }

        const candidateDocs = Array.from(candidateMap.values()).map((docSnap) => {
          const data = docSnap.data() || {};
          const updated = toDateSafe(data.updatedAt || data.createdAt);
          return {
            docSnap,
            data,
            sortValue: updated ? updated.getTime() : 0,
          };
        });

        candidateDocs.sort((a, b) => b.sortValue - a.sortValue);

        const ctxForSelection = ctx;
        const wantSpecificStep = !!(ctxForSelection.percorsoId && ctxForSelection.stepKey);
        const wantSpecificIndex = Number.isInteger(ctxForSelection.stepIndex);
        const pickCandidate = () => {
          if (!candidateDocs.length) return null;
          if (!wantSpecificStep) return candidateDocs[0];

          const strictMatch = candidateDocs.find(({ data }) => {
            const meta = data || {};
            if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
            if (meta.stepKey && meta.stepKey !== ctxForSelection.stepKey) return false;
            if (wantSpecificIndex) {
              const docIndex = toStepIndex(meta.stepIndex);
              return docIndex === ctxForSelection.stepIndex;
            }
            return true;
          });
          if (strictMatch) return strictMatch;

          if (!wantSpecificIndex) {
            return candidateDocs.find(({ data }) => {
              const meta = data || {};
              if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
              if (meta.stepKey && meta.stepKey !== ctxForSelection.stepKey) return false;
              return true;
            }) || null;
          }

          return null;
        };

        const selected = pickCandidate();

        if (selected) {
          const { docSnap, data } = selected;
          questionarioDocId.value = docSnap.id;
          loadedDocUid.value = data.uid || '';
          setDocContext(data);
          questionarioInfo.value = {
            createdAt: toDateSafe(data.createdAt) || null,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || null,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
          };
          setFormData(data.answers || {}, data);
          allowPartialSubmission.value = !!data.allowPartial;
          manualStandalone.value = isTruthyManualStandalone(data.manualStandalone) || manualStandalone.value;
          await maybeBackfillQuestionarioDoc(docSnap.id, data);
          await syncQuestionarioStepMetadata(docSnap.id, {
            templateId: data.templateId || QUESTIONARIO_TEMPLATE.id,
            templateKey: data.templateKey || data.questionarioTemplateKey || QUESTIONARIO_TEMPLATE.key,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
            templateVersion: data.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
            createdAt: toDateSafe(data.createdAt) || undefined,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || undefined,
            questionarioKey: data.questionarioKey,
            questionarioUid: data.questionarioUid || docSnap.id,
          });
          const ctx = percorsoContext.value;
          if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
            await evaluatePercorsoLock();
          }
        } else {
          questionarioDocId.value = '';
          loadedDocUid.value = '';
          clearDocContext();
          questionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label };
          setFormData({}, null);
          allowPartialSubmission.value = false;
          manualStandalone.value = isTruthyManualStandalone(pickQueryValue('manualStandalone'));
          submittedLock.value = false;
        }
      } catch (error) {
        console.error('Errore caricando il questionario:', error);
        errorMessage.value = 'Impossibile recuperare il questionario.';
        questionarioDocId.value = '';
        loadedDocUid.value = '';
        clearDocContext();
        questionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label };
        setFormData({});
        allowPartialSubmission.value = false;
        manualStandalone.value = isTruthyManualStandalone(pickQueryValue('manualStandalone'));
        submittedLock.value = false;
      } finally {
        activeSectionIndex.value = 0;
        loading.value = false;
      }
    };

    const saveQuestionario = async () => {
      if (saving.value || loading.value) return false;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return false;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return false;
      }
      const uid = effectiveUserId.value;
      if (!uid) {
        showActionMessage('Devi essere autenticata per salvare il questionario.', 'error');
        return false;
      }
      if ([uploadingPhoto, uploadingIdoneita, uploadingCertification].some((state) => Object.values(state).some(Boolean))) {
        showActionMessage('Attendi il completamento del caricamento di foto e file prima di salvare o inviare.', 'error');
        return false;
      }
      if (isManualStandalone.value) {
        const validationErrors = validateRequiredFields();
        missingRequiredFields.value = validationErrors;
        if (validationErrors.length) {
          successMessage.value = '';
          showActionMessage('Seleziona i due consensi privacy obbligatori e accetta i termini e condizioni prima di salvare.', 'error');
          alertMissingFields(validationErrors);
          return false;
        }
      }

      saving.value = true;
      showActionMessage('');
      successMessage.value = '';

      try {
        const savedAnswers = plainAnswers();
        const docId = await ensureQuestionarioDoc();
        const docRef = doc(db, QUESTIONARI_COLLECTION, docId);
        await updateDoc(docRef, buildQuestionarioUpdatePayload({
          answers: savedAnswers,
          templateVersion: QUESTIONARIO_TEMPLATE.version,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
          templateId: QUESTIONARIO_TEMPLATE.id,
          templateKey: QUESTIONARIO_TEMPLATE.key,
          questionarioUid: docId,
        }));

        const now = new Date();
        questionarioInfo.value = {
          createdAt: questionarioInfo.value.createdAt || now,
          updatedAt: now,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
        };
        await syncQuestionarioStepMetadata(docId, {
          updatedAt: now,
          templateVersion: QUESTIONARIO_TEMPLATE.version,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
          templateId: QUESTIONARIO_TEMPLATE.id,
          templateKey: QUESTIONARIO_TEMPLATE.key,
          adminStatus: null,
        });
        await clearAdminStatusOnStep();
        lastLoaded.value = deepClone(savedAnswers);
        dirty.value = JSON.stringify(plainAnswers()) !== JSON.stringify(savedAnswers);
        successMessage.value = isManualStandalone.value
          ? 'Il questionario è stato salvato correttamente e verrà messo in lavorazione.'
          : 'Il questionario è stato salvato correttamente, potresti essere contattata per ulteriori chiarimenti.';
        if (successTimer) clearTimeout(successTimer);
        successTimer = setTimeout(() => {
          successMessage.value = '';
        }, 4000);
        return true;
      } catch (error) {
        console.error('Errore nel salvataggio del questionario:', error);
        showActionMessage('Errore durante il salvataggio del questionario. Le risposte restano disponibili: riprova.', 'error');
        return false;
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      setFormData(lastLoaded.value);
      missingRequiredFields.value = [];
    };

    const markStepCompleted = async () => {
      if (completingStep.value || saving.value || loading.value) return;
      if ([uploadingPhoto, uploadingIdoneita, uploadingCertification].some((state) => Object.values(state).some(Boolean))) {
        showActionMessage('Attendi il completamento del caricamento di foto e file prima di inviare.', 'error');
        return;
      }
      if (isManualStandalone.value) return;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return;
      }
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
        showActionMessage('Impossibile determinare lo step da completare.', 'error');
        return;
      }
      const validationErrors = validateRequiredFields();
      missingRequiredFields.value = validationErrors;
      if (validationErrors.length) {
        showActionMessage('Completa i campi obbligatori prima di inviare il questionario.', 'error');
        alertMissingFields(validationErrors);
        return;
      }
      try {
        completingStep.value = true;
        if (dirty.value || !questionarioDocId.value) {
          const saved = await saveQuestionario();
          if (!saved) return;
        }
        if (dirty.value) {
          showActionMessage('Hai modificato alcune risposte durante il salvataggio. Controllale e premi di nuovo Invia.', 'error');
          return;
        }
        if (!questionarioDocId.value) {
          showActionMessage('Salva prima il questionario per poterlo completare.', 'error');
          return;
        }
        await setStepDone(ctx.percorsoId, ctx.stepIndex, true);
        await clearAdminStatusOnStep();
        showActionMessage('Step segnato come completato.');
        stepDoneFlag.value = true;
        adminNeedsUpdate.value = false;
        submittedLock.value = true;
        missingRequiredFields.value = [];
      } catch (error) {
        console.error('markStepCompleted error', error);
        showActionMessage('Errore durante il completamento dello step.', 'error');
      } finally {
        completingStep.value = false;
      }
    };

    const uploadSingleFile = async (segments, file) => {
      const uid = effectiveUserId.value;
      if (isAdminApproved.value) {
        const message = 'Questionario già approvato';
        showApprovedLockMessage();
        throw Object.assign(new Error(message), { userMessage: message });
      }
      if (isHardLocked.value) {
        const message =
          'Questionario già inviato: contatta il supporto per riaprire il caricamento dei file.';
        showActionMessage(message, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        throw Object.assign(new Error(message), { userMessage: message });
      }
      if (!uid) {
        const message = 'Sessione scaduta: effettua di nuovo il login per caricare file e foto.';
        showActionMessage(message, 'error');
        throw Object.assign(new Error(message), { userMessage: message });
      }
      const docId = await ensureQuestionarioDoc();
      const fileName = `${Date.now()}_${sanitizeName(file.name)}`;
      const path = makeStoragePath(uid, docId, ...segments, fileName);
      const ref = storageRef(storage, path);
      await uploadBytesResumable(ref, file);
      const url = await getDownloadURL(ref);
      return { name: file.name, size: file.size, contentType: file.type || null, path, url };
    };

    const onPhotoChange = async (fieldKey, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setUploadState(uploadingPhoto, fieldKey, true);
      try {
        const metadata = await uploadSingleFile(['foto', fieldKey], file);
        suppressDirty.value = true;
        form.foto[fieldKey] = metadata;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando la foto:', error);
        showActionMessage(error.userMessage || 'Errore durante il caricamento della foto. Riprova.', 'error');
      } finally {
        setUploadState(uploadingPhoto, fieldKey, false);
      }
    };

    const removePhoto = async (fieldKey) => {
      const metadata = form.foto[fieldKey];
      if (!metadata) return;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return;
      }
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      try {
        await deleteObject(storageRef(storage, metadata.path));
        suppressDirty.value = true;
        form.foto[fieldKey] = null;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando la foto:', error);
        showActionMessage('Errore durante l\'eliminazione della foto. Riprova.', 'error');
      }
    };

    const onIdoneitaFileChange = async (fieldKey, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setUploadState(uploadingIdoneita, fieldKey, true);
      try {
        const metadata = await uploadSingleFile(['idoneita', fieldKey], file);
        suppressDirty.value = true;
        form.idoneita[fieldKey] = metadata;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando il file:', error);
        showActionMessage(error.userMessage || 'Errore durante il caricamento del file. Riprova.', 'error');
      } finally {
        setUploadState(uploadingIdoneita, fieldKey, false);
      }
    };

    const removeIdoneitaFile = async (fieldKey) => {
      const metadata = form.idoneita[fieldKey];
      if (!metadata) return;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return;
      }
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      try {
        await deleteObject(storageRef(storage, metadata.path));
        suppressDirty.value = true;
        form.idoneita[fieldKey] = null;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando il file:', error);
        showActionMessage('Errore durante l\'eliminazione del file. Riprova.', 'error');
      }
    };

    const addGravidanza = () => {
      form.gravidanze.push({ anno: '', parto: '' });
    };
    const removeGravidanza = (index) => {
      form.gravidanze.splice(index, 1);
    };

    const addAltraPatologia = () => {
      form.patologieAltre.push({
        nome: '',
        diagnosi: '',
        certificazioneFile: null,
        note: '',
      });
    };
    const removeAltraPatologia = async (index) => {
      const item = form.patologieAltre[index];
      if (item?.certificazioneFile) {
        await deleteStoredFile(item.certificazioneFile);
      }
      form.patologieAltre.splice(index, 1);
    };

    const addProblematicaMuscolo = () => {
      form.patologieMuscoloAltre.push({
        zona: '',
        diagnosi: '',
        certificazioneFile: null,
        note: '',
      });
    };
    const removeProblematicaMuscolo = async (index) => {
      const item = form.patologieMuscoloAltre[index];
      if (item?.certificazioneFile) {
        await deleteStoredFile(item.certificazioneFile);
      }
      form.patologieMuscoloAltre.splice(index, 1);
    };

    const addInfortunio = () => {
      form.infortuni.push({ descrizione: '', anno: '', limitazioni: '' });
    };
    const removeInfortunio = (index) => {
      form.infortuni.splice(index, 1);
    };

    const addIntervento = () => {
      form.interventi.push({ descrizione: '', anno: '', limitazioni: '' });
    };
    const removeIntervento = (index) => {
      form.interventi.splice(index, 1);
    };

    const deleteStoredFile = async (metadata) => {
      if (!metadata?.path) return;
      try {
        await deleteObject(storageRef(storage, metadata.path));
      } catch (error) {
        console.error('Errore eliminando il file di certificazione:', error);
      }
    };

    const getCertificationTarget = (group, identifier) => {
      if (group === 'patologie') return form.patologie[identifier];
      if (group === 'patologieAltre') return form.patologieAltre[identifier];
      if (group === 'patologieMuscolo') return form.patologieMuscolo[identifier];
      if (group === 'patologieMuscoloAltre') return form.patologieMuscoloAltre[identifier];
      return null;
    };

    const clearCertification = (group, identifier, { removeRemote = false, silent = false } = {}) => {
      const target = getCertificationTarget(group, identifier);
      if (!target) return;
      if (removeRemote && target.certificazioneFile) {
        deleteStoredFile(target.certificazioneFile).catch(() => {});
      }
      if (silent) suppressDirty.value = true;
      target.certificazioneFile = null;
      if (silent) suppressDirty.value = false;
    };

    const makeCertificationSegments = (group, identifier) => {
      switch (group) {
        case 'patologie':
          return ['patologie', identifier, 'certificazioni'];
        case 'patologieAltre':
          return ['patologie_altre', String(identifier), 'certificazioni'];
        case 'patologieMuscolo':
          return ['patologie_muscolo', identifier, 'certificazioni'];
        case 'patologieMuscoloAltre':
          return ['patologie_muscolo_altre', String(identifier), 'certificazioni'];
        default:
          return ['certificazioni_generiche'];
      }
    };

    const onCertificationFileChange = async (group, identifier, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      showActionMessage('');
      const key = certificationUploadKey(group, identifier);
      setUploadState(uploadingCertification, key, true);
      try {
        const metadata = await uploadSingleFile(makeCertificationSegments(group, identifier), file);
        const target = getCertificationTarget(group, identifier);
        if (!target) return;
        suppressDirty.value = true;
        target.certificazioneFile = metadata;
        suppressDirty.value = false;
        const docId = await ensureQuestionarioDoc();
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, docId),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(docId, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando il file di certificazione:', error);
        showActionMessage(error.userMessage || 'Errore durante il caricamento del file di certificazione. Riprova.', 'error');
      } finally {
        setUploadState(uploadingCertification, key, false);
      }
    };

    const removeCertificationFile = async (group, identifier) => {
      const target = getCertificationTarget(group, identifier);
      const metadata = target?.certificazioneFile;
      if (!metadata) return;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return;
      }
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      showActionMessage('');
      try {
        await deleteStoredFile(metadata);
        clearCertification(group, identifier, { silent: true });
        const docId = await ensureQuestionarioDoc();
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, docId),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(docId, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando il file di certificazione:', error);
        showActionMessage('Errore durante l\'eliminazione del file di certificazione. Riprova.', 'error');
      }
    };

    const handleDiagnosiChange = (group, identifier, value) => {
      const target = getCertificationTarget(group, identifier);
      if (!target) return;
      if (value === 'Diagnosi certificata') return;
      clearCertification(group, identifier, { removeRemote: true });
    };

    const resetMetabolicBlocks = async ({ removeRemote = false } = {}) => {
      PATHOLOGIES.forEach((item) => {
        const target = form.patologie[item.key];
        target.presente = false;
        target.diagnosi = '';
        target.note = '';
        clearCertification('patologie', item.key, { removeRemote, silent: true });
      });
      if (removeRemote) {
        await Promise.all(
          form.patologieAltre
            .filter((entry) => entry?.certificazioneFile)
            .map((entry) => deleteStoredFile(entry.certificazioneFile)),
        );
      }
      form.patologieAltre.splice(0, form.patologieAltre.length);
      form.dichiarazionePatologie = false;
    };

    const resetMuscoloBlocks = async ({ removeRemote = false } = {}) => {
      MUSCLE_PATHOLOGIES.forEach((item) => {
        const target = form.patologieMuscolo[item.key];
        target.presente = false;
        target.diagnosi = '';
        target.note = '';
        if (item.askCm) target.cm = '';
        clearCertification('patologieMuscolo', item.key, { removeRemote, silent: true });
      });
      if (removeRemote) {
        await Promise.all(
          form.patologieMuscoloAltre
            .filter((entry) => entry?.certificazioneFile)
            .map((entry) => deleteStoredFile(entry.certificazioneFile)),
        );
      }
      form.patologieMuscoloAltre.splice(0, form.patologieMuscoloAltre.length);
      form.infortuni.splice(0, form.infortuni.length);
      form.interventi.splice(0, form.interventi.length);
      form.noteLimitazioni = '';
      form.farmaciAssunti = '';
    };

    watch(
      () => form.integratoriUsati,
      (value) => {
        if (value !== 'Si') form.integratoriUsatiQuali = '';
      },
    );

    watch(
      () => form.integratoriAttuali,
      (value) => {
        if (value !== 'Si') form.integratoriAttualiQuali = '';
      },
    );

    watch(
      () => form.attrezzaturaCasa.includes('Altro'),
      (hasAltro) => {
        if (!hasAltro) form.attrezzaturaCasaAltro = '';
      },
    );

    watch(
      () => [birthdateParts.day, birthdateParts.month, birthdateParts.year],
      () => {
        updateBirthdateFromParts();
      },
    );

    watch(
      () => form.dataNascita,
      (value) => {
        syncBirthdatePartsFromValue(value);
      },
    );

    watch(allowPartialSubmission, () => {
      if (!missingRequiredFields.value.length) return;
      missingRequiredFields.value = validateRequiredFields();
    });

    PATHOLOGIES.forEach((item) => {
      watch(
        () => form.patologie[item.key].presente,
        (value) => {
          if (!value) {
            form.patologie[item.key].diagnosi = '';
            clearCertification('patologie', item.key, { removeRemote: true });
            form.patologie[item.key].note = '';
          }
        },
      );
      watch(
        () => form.patologie[item.key].diagnosi,
        (value) => {
          handleDiagnosiChange('patologie', item.key, value);
        },
      );
    });

    MUSCLE_PATHOLOGIES.forEach((item) => {
      watch(
        () => form.patologieMuscolo[item.key].presente,
        (value) => {
          if (!value) {
            form.patologieMuscolo[item.key].diagnosi = '';
            clearCertification('patologieMuscolo', item.key, { removeRemote: true });
            form.patologieMuscolo[item.key].note = '';
            if (item.askCm) form.patologieMuscolo[item.key].cm = '';
          }
        },
      );
      watch(
        () => form.patologieMuscolo[item.key].diagnosi,
        (value) => {
          handleDiagnosiChange('patologieMuscolo', item.key, value);
        },
      );
    });

    watch(
      () => form.haPatologieMetaboliche,
      async (value, previous) => {
        if (suppressDirty.value) return;
        if (value === 'Si') return;
        suppressDirty.value = true;
        try {
          await resetMetabolicBlocks({ removeRemote: value === 'No' && previous === 'Si' });
          if (value === '') form.haPatologieMetaboliche = 'No';
        } finally {
          suppressDirty.value = false;
        }
      },
    );

    watch(
      () => form.haPatologieMuscolo,
      async (value, previous) => {
        if (suppressDirty.value) return;
        if (value === 'Si') return;
        suppressDirty.value = true;
        try {
          await resetMuscoloBlocks({ removeRemote: value === 'No' && previous === 'Si' });
          if (value === '') form.haPatologieMuscolo = 'No';
        } finally {
          suppressDirty.value = false;
        }
      },
    );

    watch(
      () => form.allenamentoAttuale,
      (value) => {
        if (value !== 'Si') form.tipiAllenamento = [];
      },
    );

    watch(
      form,
      () => {
        if (!suppressDirty.value) dirty.value = true;
        if (missingRequiredFields.value.length) {
          missingRequiredFields.value = validateRequiredFields();
        }
      },
      { deep: true },
    );

    watch(activeSectionIndex, () => scrollToActiveSection());

    watch(
      questionarioTargetKey,
      (next, prev) => {
        if (prev !== undefined && next !== prev) {
          loadQuestionario();
        }
      },
    );

    watch(
      () => currentUserId.value,
      (next, prev) => {
        if (next && next !== prev) {
          loadQuestionario();
        }
      },
    );

    onMounted(() => {
      detachAuthListener = onAuthStateChanged(auth, (user) => {
        const nextUid = user?.uid || '';
        const prevUid = currentUserId.value;
        if (nextUid !== prevUid) {
          currentUserId.value = nextUid;
        }
        authResolved.value = true;
        if (nextUid) {
          loadQuestionario();
        } else {
          loading.value = false;
        }
      });

      scrollToActiveSection();

    });

    onBeforeUnmount(() => {
      if (successTimer) clearTimeout(successTimer);
      if (actionTimer) clearTimeout(actionTimer);
      if (typeof detachAuthListener === 'function') {
        detachAuthListener();
      }
    });

    return {
      form,
      loading,
      saving,
      dirty,
      errorMessage,
      lockWarning,
      successMessage,
      missingRequiredFields,
      isFieldRequired,
      questionarioInfo,
      allowPartialSubmission,
      uploadingPhoto,
      uploadingIdoneita,
      adminNeedsUpdate,
      isAdminApproved,
      isHardLocked,
      isCertificationUploading,
      isImageFile,
      isPdfFile,
      effectiveUserId,
      TURNISTA_OPTIONS,
      WORK_HOURS_OPTIONS,
      LIFESTYLE_OPTIONS,
      SLEEP_HOURS_OPTIONS,
      SLEEP_QUALITY_OPTIONS,
      YES_NO_OPTIONS,
      birthdateParts,
      DAY_OPTIONS,
      MONTH_OPTIONS,
      YEAR_OPTIONS,
      ALLENAMENTO_ATTUALE_OPTIONS,
      TIPI_ALLENAMENTO,
      ESPERIENZA_SALA_OPTIONS,
      PT_OPTIONS,
      ALVO_OPTIONS,
      CICLO_MESTRUALE_OPTIONS,
      ACQUA_OPTIONS,
      ALCOL_OPTIONS,
      DIAGNOSIS_OPTIONS,
      REGIME_ALIMENTARE_OPTIONS,
      PASTI_OPTIONS,
      SALTARE_PASTI_OPTIONS,
      LUOGO_ALLENAMENTO_OPTIONS,
      FREQUENZA_ALLENAMENTO_OPTIONS,
      DURATA_ALLENAMENTO_OPTIONS,
      ATTREZZATURE,
      AUTOCERTIFICAZIONE_TEMPLATE,
      PATHOLOGIES,
      MUSCLE_PATHOLOGIES,
      formatDate: formatDateUtils,
      sectionFlow,
      activeSectionIndex,
      currentSection,
      nextSectionTitle,
      isFirstSection,
      isLastSection,
      progressValue,
      goToSection,
      goToNextSection,
      handleFormSubmit,
      stepHeadingRef,
      actionMessageRef,
      requiredSummaryRef,
      goToMissingField,
      isManualStandalone,
      goToPreviousSection,
      isSectionActive,
      formRootRef,
      saveQuestionario,
      resetForm,
      goBack,
      actionMessage,
      actionMessageType,
      exportFormAsPdf,
      pdfExporting,
      completingStep,
      canCompleteStep,
      showCompleteButton,
      addGravidanza,
      removeGravidanza,
      addAltraPatologia,
      removeAltraPatologia,
      addProblematicaMuscolo,
      removeProblematicaMuscolo,
      addInfortunio,
      removeInfortunio,
      addIntervento,
      removeIntervento,
      onPhotoChange,
      removePhoto,
      onIdoneitaFileChange,
      removeIdoneitaFile,
      onCertificationFileChange,
      removeCertificationFile,
      handleDiagnosiChange,
      markStepCompleted,
      linkMismatchWarning,
      loginRoute,
    };
  },
};
</script>

<style scoped src="./styles/ProgrammiUserForm.css"></style>
